// =============================================================================
// Writing Tool - V1.5 Simplified Journalist Editor
// A clean, focused writing experience with AI assistance
// =============================================================================

import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  ArrowLeft,
  Save,
  Loader2,
  Sparkles,
  RotateCcw,
  Check,
  AlertTriangle,
  Plus,
  FileText,
  Trash2,
  Clock,
  Split,
  Languages,
  X,
  Share2,
  Copy,
  CheckCircle2,
  Lock,
  Unlock,
  RefreshCw,
  ChevronDown,
  StickyNote,
  MessageSquare,
  History,
  Send,
  Link2 as LinkIcon,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { diffWords, getDiffStats } from '../utils/diff';
import {
  SimpleStory,
  StoryGroup,
  createSimpleStory,
  getSimpleStory,
  getSimpleStories,
  getStoryGroups,
  getStoryGroup,
  getCanonicalStory,
  getStoryDerivatives,
  saveSimpleStory,
  setReviewedStatus,
  setDraftStatus,

  unlockStory,
  isStoryLocked,
  archiveSimpleStory,
  createTranslation,
  createDerivativeStory,
  getPlatformLanguageConfig,
  getTvPacingConfig,
  getTvWordTargets,
  getStoryGridList,
  LanguageCode as StoreLanguageCode,
  getFactsForGroup,
  getExistingDerivative,
  getDerivativeCombinations,
  createAllDerivativeDrafts,
  checkoutStory,
  releaseCheckout,
  heartbeatCheckout,
  VersionConflictError,
  submitForReview,
  editorApproveStory,
  editorRejectStory,
} from '../services/simpleStore';
import { useUserRole } from '../hooks/useUserRole';
import StoryGroupCard from '../components/StoryGroupCard';
import StoryGrid from '../components/StoryGrid';
import LanguageSelector from '../components/LanguageSelector';
import FactsPanel from '../components/FactsPanel';
import VersionHistory from '../components/VersionHistory';
import EditorReviewPanel from '../components/EditorReviewPanel';
import {
  runJournalistAgent,
  runTvJournalistAgent,
  runParallelCritics,
  runFactConsensusPromotion,
  runCuratorAgent,
  runTranslatorAgent,
  runWebRewriterAgent,
  runTranslatorAgentV2,
  runSocialGeneratorAgent,
  AgentError,
  CuratorResponse,
  FactForVerification,
} from '../services/geminiService';
import {
  LanguageCode,
  Platform,
  EditorIssue,
  EditorFeedbackCard,
  LANGUAGE_LABELS,
  LANGUAGE_NAMES,
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  TvFormat,
  TV_FORMAT_LABELS,
  DEFAULT_TV_FORMAT,
  DEFAULT_PLATFORM,
  TV_FORMAT_DURATION_TARGETS,
  StoryGridData,
  estimateDuration,
  estimateDurationDetailed,
  formatDuration,
  getDurationStatusColor,
  getDurationStatusIcon,
  countWords,
  PlatformLanguageConfig,
  ReviewMode,
  TvJournalistAgentResponse,
  TvPacingConfig,
  TvWordTargets,
  DEFAULT_PACING,
  DurationBreakdown,
  TranslatorResponse,
} from '../types';

// RTL languages
const RTL_LANGUAGES: LanguageCode[] = ['fa-AF', 'ps-AF'];
const isRTL = (lang: LanguageCode) => RTL_LANGUAGES.includes(lang);

// Format relative time for "Last saved" indicator
const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);

  if (diffSecs < 10) return 'just now';
  if (diffSecs < 60) return `${diffSecs}s ago`;
  if (diffMins < 60) return `${diffMins}m ago`;
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Status priority to keep active work at the top of the list
// Phase 3: Updated status values
const STATUS_ORDER: Record<string, number> = {
  draft: 1,
  reviewed: 2,
  approved: 3,
  archived: 4,
};

// Detect if text content doesn't match selected language
// Pashto-exclusive characters not used in standard Dari
const PASHTO_CHARS = /[ښځڅډړږټګڼ]/g;
const PASHTO_CHAR_THRESHOLD = 3;

type LanguageMismatchResult = {
  message: string;
  suggestedLang: LanguageCode;
} | null;

const detectLanguageMismatch = (text: string, selectedLang: LanguageCode): LanguageMismatchResult => {
  if (!text || text.length < 20) return null;

  const arabicRegex = /[\u0600-\u06FF\u0750-\u077F]/g;
  const latinRegex = /[a-zA-Z]/g;

  const sample = text.slice(0, 1000);
  const arabicCount = (sample.match(arabicRegex) || []).length;
  const latinCount = (sample.match(latinRegex) || []).length;

  if (selectedLang === 'eng') {
    if (arabicCount > latinCount && arabicCount > 10) {
      // English selected but Arabic-script content — guess Dari vs Pashto
      const pashtoCount = (sample.match(PASHTO_CHARS) || []).length;
      const guessedLang: LanguageCode = pashtoCount >= PASHTO_CHAR_THRESHOLD ? 'ps-AF' : 'fa-AF';
      return {
        message: `It looks like you're writing in ${LANGUAGE_NAMES[guessedLang]}, but 'English' is selected.`,
        suggestedLang: guessedLang,
      };
    }
  } else {
    if (latinCount > arabicCount && latinCount > 10) {
      return {
        message: "It looks like you're writing in English, but a local language is selected.",
        suggestedLang: 'eng',
      };
    }
    // Dari vs Pashto check
    const pashtoCount = (sample.match(PASHTO_CHARS) || []).length;
    if (selectedLang === 'fa-AF' && pashtoCount >= PASHTO_CHAR_THRESHOLD) {
      return {
        message: `Content contains Pashto-specific characters (ښ، ځ، څ، ډ، ړ، ږ، ټ) but language is set to Dari.`,
        suggestedLang: 'ps-AF',
      };
    }
    if (selectedLang === 'ps-AF' && pashtoCount === 0 && arabicCount > 20) {
      return {
        message: `Content appears to be Dari — no Pashto-specific characters (ښ، ځ، څ، ډ، ړ، ږ، ټ) detected.`,
        suggestedLang: 'fa-AF',
      };
    }
  }
  return null;
};

/**
 * Helper to check if an AI agent response is an error.
 * Handles both AgentError objects and raw string responses.
 */
const isAgentError = (response: unknown): response is AgentError => {
  if (typeof response === 'string') {
    return false; // Raw string response, not an error
  }
  if (response && typeof response === 'object' && 'error' in response) {
    return (response as AgentError).error === true;
  }
  return false;
};

/**
 * Helper to parse a response that might be a string or already parsed object.
 * Some AI agents return raw JSON strings that need parsing.
 */
const parseAgentResponse = <T,>(response: unknown): T | null => {
  if (typeof response === 'string') {
    try {
      return JSON.parse(response) as T;
    } catch {
      console.warn('[parseAgentResponse] Failed to parse string response');
      return null;
    }
  }
  return response as T;
};

const WritingTool: React.FC = () => {
  // User role
  const { userId, isEditor, isJournalist, fullName } = useUserRole();

  // View state
  const [view, setView] = useState<'list' | 'editor'>('list');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Story list (grouped)
  const [storyGroups, setStoryGroups] = useState<StoryGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<StoryGroup | null>(null);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);

  // Current story being edited
  const [currentStory, setCurrentStory] = useState<SimpleStory | null>(null);
  const [currentGroup, setCurrentGroup] = useState<StoryGroup | null>(null);

  // Phase 3: Locked state for approved stories
  const isApprovalLocked = currentStory?.status === 'approved';
  const [showUnlockWarning, setShowUnlockWarning] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  // ENPS Parity: Pessimistic locking (checkout)
  const [isCheckedOutByOther, setIsCheckedOutByOther] = useState(false);
  const [lockHolderName, setLockHolderName] = useState('');
  const hasCheckoutLock = useRef(false); // true only when THIS user holds the lock
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const heartbeatFailCount = useRef(0); // consecutive heartbeat failures
  const [heartbeatWarning, setHeartbeatWarning] = useState(false); // show connection warning

  // Editor workflow: pending_review status
  const isPendingReview = currentStory?.status === 'pending_review';

  // TEMP: bypass author check for testing editor review panel (remove before production)
  const BYPASS_AUTHOR_CHECK = true;
  const isNotAuthor = BYPASS_AUTHOR_CHECK || currentStory?.authorId !== userId;

  // Unified isLocked: for journalists, locked at pending_review + approved; editors can edit anytime
  const isReadOnlyForRole = (isPendingReview || isApprovalLocked) && !isEditor;
  const isLocked = isReadOnlyForRole || isCheckedOutByOther;

  // Editor workflow: rejection dialog
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [submittingForReview, setSubmittingForReview] = useState(false);

  // ENPS Parity: Scratchpad & Editorial Notes
  const [scratchpad, setScratchpad] = useState('');
  const [editorialNotes, setEditorialNotes] = useState('');
  const [showScratchpad, setShowScratchpad] = useState(false);

  // Phase 3: Story Grid data for list view
  const [storyGridList, setStoryGridList] = useState<StoryGridData[]>([]);
  const [useGridView, setUseGridView] = useState(true); // Default to grid view

  // Phase 3: Derivative generation
  const [generatingDerivative, setGeneratingDerivative] = useState(false);
  const isGeneratingDerivativeRef = useRef(false); // sync guard against double-submit race
  const lastTextSelectionRef = useRef<{ text: string; location: EditorFeedbackCard['location'] } | null>(null);
  const [showDerivativeModal, setShowDerivativeModal] = useState(false);
  const [derivativeTarget, setDerivativeTarget] = useState<{ platform: Platform; language: LanguageCode; sourceStory?: SimpleStory } | null>(null);
  const [generatingCells, setGeneratingCells] = useState<Array<{ groupId: string; platform: Platform; language: LanguageCode }>>([]);

  // Editor form state - Web article fields
  const [headline, setHeadline] = useState('');
  const [deck, setDeck] = useState('');
  const [body, setBody] = useState('');
  const [language, setLanguage] = useState<LanguageCode>(DEFAULT_LANGUAGE);
  const [tvFormat, setTvFormat] = useState<TvFormat>(DEFAULT_TV_FORMAT);

  // Editor form state - TV script fields
  const [slug, setSlug] = useState('');
  const [anchorIntro, setAnchorIntro] = useState('');
  const [script, setScript] = useState('');

  // Determine if we should show TV editor or Web editor
  // - New stories: always TV (default platform)
  // - Existing TV stories with TV fields: show TV editor
  // - Existing TV stories with only web fields (legacy): show web editor
  const hasTvContent = currentStory?.platformMetadata?.slug || currentStory?.platformMetadata?.anchorIntro || currentStory?.platformMetadata?.script;
  const isNewStory = !currentStory;
  const isTvPlatform = currentStory?.platform === 'tv' || isNewStory;
  const useTvEditor = isTvPlatform && (isNewStory || hasTvContent || !currentStory?.headline);

  // TV pacing configuration (loaded from database)
  const [pacingConfig, setPacingConfig] = useState<TvPacingConfig | null>(null);
  const [baseWordTargets, setBaseWordTargets] = useState<TvWordTargets | null>(null);
  // Custom duration targets (in seconds) - journalist sets these, we calculate words
  const [customDurationTargets, setCustomDurationTargets] = useState<{ anchor?: number; script?: number } | null>(null);
  const [showDurationTargetEditor, setShowDurationTargetEditor] = useState(false);

  // Load pacing config when language changes
  useEffect(() => {
    if (!useTvEditor) return;
    const loadPacingConfig = async () => {
      const config = await getTvPacingConfig(language);
      setPacingConfig(config);
    };
    loadPacingConfig();
  }, [useTvEditor, language]);

  // Load base word targets when format or language changes
  useEffect(() => {
    if (!useTvEditor) return;
    const loadWordTargets = async () => {
      const targets = await getTvWordTargets(tvFormat, language);
      setBaseWordTargets(targets);
      setCustomDurationTargets(null); // Reset custom targets when format/language changes
    };
    loadWordTargets();
  }, [useTvEditor, tvFormat, language]);

  // Calculate effective duration targets (custom overrides or defaults from database)
  const effectiveDurationTargets = useMemo(() => {
    if (!baseWordTargets || !pacingConfig) return null;
    // Default durations: calculate from base word targets
    const defaultAnchorDuration = Math.round(baseWordTargets.anchorWords / pacingConfig.anchorWps);
    const defaultScriptDuration = Math.round(baseWordTargets.scriptWords / pacingConfig.scriptWps);
    return {
      anchorDuration: customDurationTargets?.anchor ?? defaultAnchorDuration,
      scriptDuration: customDurationTargets?.script ?? defaultScriptDuration,
    };
  }, [baseWordTargets, pacingConfig, customDurationTargets]);

  // Calculate word targets from duration targets (journalist sets time, we calculate words)
  const effectiveWordTargets = useMemo((): TvWordTargets | null => {
    if (!effectiveDurationTargets || !pacingConfig || !baseWordTargets) return null;
    return {
      anchorWords: Math.round(effectiveDurationTargets.anchorDuration * pacingConfig.anchorWps),
      scriptWords: Math.round(effectiveDurationTargets.scriptDuration * pacingConfig.scriptWps),
      durationTarget: effectiveDurationTargets.anchorDuration + effectiveDurationTargets.scriptDuration,
    };
  }, [effectiveDurationTargets, pacingConfig, baseWordTargets]);

  // Calculate detailed duration breakdown with language-specific pacing
  const durationBreakdown = useMemo((): DurationBreakdown | null => {
    if (!useTvEditor || !pacingConfig) return null;
    return estimateDurationDetailed(anchorIntro, script, pacingConfig, effectiveWordTargets || undefined);
  }, [useTvEditor, anchorIntro, script, pacingConfig, effectiveWordTargets]);

  // Legacy duration values for backward compatibility
  const durationEstimate = useMemo(() => {
    if (durationBreakdown) return durationBreakdown.totalDuration;
    if (!useTvEditor) return 0;
    return estimateDuration(anchorIntro, script);
  }, [durationBreakdown, useTvEditor, anchorIntro, script]);

  const durationTarget = useMemo(() => {
    return effectiveWordTargets?.durationTarget || TV_FORMAT_DURATION_TARGETS[tvFormat] || 45;
  }, [effectiveWordTargets, tvFormat]);

  // Platform-Language configuration (controls which combos are valid)
  // MUST be declared before useMemo hooks that reference it
  const [platformLanguageConfig, setPlatformLanguageConfig] = useState<PlatformLanguageConfig[]>([]);

  // Determine review mode based on whether story is canonical or derivative
  const reviewMode: ReviewMode = useMemo(() => {
    // Derivatives (translations, web from TV) get light review (style only)
    // Canonical stories get full review (all 4 critics)
    return currentStory?.isCanonical === false ? 'light' : 'full';
  }, [currentStory?.isCanonical]);

  // Filter available languages based on current platform
  const availableLanguages = useMemo(() => {
    if (!platformLanguageConfig.length) return SUPPORTED_LANGUAGES;
    const platform = currentStory?.platform || DEFAULT_PLATFORM;
    const allowed = platformLanguageConfig
      .filter(c => c.platform === platform && c.allowed)
      .map(c => c.language);
    return allowed.length > 0 ? allowed : SUPPORTED_LANGUAGES;
  }, [platformLanguageConfig, currentStory?.platform]);

  // Check if current platform-language combination is invalid
  const isInvalidPlatformLanguage = useMemo(() => {
    if (!platformLanguageConfig.length) return false;
    const platform = currentStory?.platform || DEFAULT_PLATFORM;
    const currentLang = currentStory?.language || language;
    const config = platformLanguageConfig.find(
      c => c.platform === platform && c.language === currentLang
    );
    return config && !config.allowed;
  }, [platformLanguageConfig, currentStory?.platform, currentStory?.language, language]);

  // AI Review state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiLoadingMsg, setAiLoadingMsg] = useState('');
  const [aiIssues, setAiIssues] = useState<EditorIssue[]>([]);
  const [showAiPanel, setShowAiPanel] = useState(false);

  // Version History sidebar
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  // Editor Review Panel
  const [showEditorReviewPanel, setShowEditorReviewPanel] = useState(false);
  const [editorFeedback, setEditorFeedback] = useState<EditorFeedbackCard[]>([]);

  // Diff comparison (track changes)
  const [originalBody, setOriginalBody] = useState('');
  const [originalHeadline, setOriginalHeadline] = useState('');
  const [originalDeck, setOriginalDeck] = useState('');
  // TV original fields for track changes
  const [originalSlug, setOriginalSlug] = useState('');
  const [originalAnchorIntro, setOriginalAnchorIntro] = useState('');
  const [originalScript, setOriginalScript] = useState('');
  const [showDiff, setShowDiff] = useState(false);

  // Messages
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [versionConflict, setVersionConflict] = useState(false);

  // Language mismatch warning
  const [languageWarning, setLanguageWarning] = useState<LanguageMismatchResult>(null);
  const [langWarningSource, setLangWarningSource] = useState<'save' | 'aiReview'>('save');

  // Translation state
  const [showTranslateModal, setShowTranslateModal] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [availableTranslationLanguages, setAvailableTranslationLanguages] = useState<LanguageCode[]>([]);
  const [loadingTranslationLanguages, setLoadingTranslationLanguages] = useState(false);

  // Social posts state
  const [generatingSocialPosts, setGeneratingSocialPosts] = useState(false);
  const [socialPostsError, setSocialPostsError] = useState<string | null>(null);

  // Auto-save state
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Facts panel refresh key (incremented when facts are updated by critics)
  const [factsRefreshKey, setFactsRefreshKey] = useState(0);

  // Note: Removed body scroll lock - AI now uses non-blocking toast notification

  // Track unsaved changes
  useEffect(() => {
    if (currentStory) {
      const hasChanges =
        headline !== currentStory.headline ||
        deck !== currentStory.deck ||
        body !== currentStory.body;
      setHasUnsavedChanges(hasChanges);
    } else if (headline.trim() || deck.trim() || body.trim()) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [headline, deck, body, currentStory]);

  // Auto-save after 5 seconds of inactivity (only for existing stories with changes)
  useEffect(() => {
    if (!hasUnsavedChanges || !currentStory || !headline.trim() || saving || aiLoading) return;

    const timer = setTimeout(async () => {
      console.log('[Auto-save] Saving changes...');
      setSaving(true);
      try {
        const updated = await saveSimpleStory(currentStory.id, {
          headline,
          deck,
          body,
        }, currentStory.version);
        if (updated) {
          setCurrentStory(updated);
          setLastSaved(new Date());
          setHasUnsavedChanges(false);
        }
      } catch (error) {
        if (error instanceof VersionConflictError) {
          setVersionConflict(true);
          return;
        }
        console.error('[Auto-save] Failed:', error);
      } finally {
        setSaving(false);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [headline, deck, body, hasUnsavedChanges, currentStory, saving, aiLoading]);

  // Warn on page unload if unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // ENPS Parity: Heartbeat — keep checkout lock alive every 60s
  // Only runs if THIS user acquired the lock (hasCheckoutLock.current === true)
  useEffect(() => {
    const storyId = currentStory?.id;
    if (!storyId) return;

    // Clear any previous heartbeat
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }

    // Only start heartbeat if we hold the lock
    if (hasCheckoutLock.current) {
      heartbeatFailCount.current = 0;
      setHeartbeatWarning(false);
      heartbeatRef.current = setInterval(async () => {
        if (!hasCheckoutLock.current) return;
        const ok = await heartbeatCheckout(storyId);
        if (ok) {
          heartbeatFailCount.current = 0;
          setHeartbeatWarning(false);
        } else {
          heartbeatFailCount.current += 1;
          console.warn(`[Heartbeat] Failed (consecutive: ${heartbeatFailCount.current})`);
          if (heartbeatFailCount.current >= 2) {
            setHeartbeatWarning(true);
          }
        }
      }, 60_000);
    }

    // Cleanup: release lock on unmount or story change
    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      if (hasCheckoutLock.current) {
        // Fire-and-forget release — navigator.sendBeacon isn't needed since
        // the 3-minute stale timeout handles unclean exits gracefully
        releaseCheckout(storyId);
        hasCheckoutLock.current = false;
      }
    };
  }, [currentStory?.id]);

  // Also release lock on beforeunload (best-effort for browser close/refresh)
  useEffect(() => {
    const handleUnloadReleaseLock = () => {
      if (hasCheckoutLock.current && currentStory?.id) {
        // Use sendBeacon for best-effort on tab close — fetch may be cancelled
        // Fall back to stale lock timeout if this fails
        releaseCheckout(currentStory.id);
      }
    };
    window.addEventListener('beforeunload', handleUnloadReleaseLock);
    return () => window.removeEventListener('beforeunload', handleUnloadReleaseLock);
  }, [currentStory?.id]);

  // Load stories and platform-language config on mount
  useEffect(() => {
    loadStories();

    // Load platform-language config (controls which combos are valid)
    getPlatformLanguageConfig().then(config => {
      setPlatformLanguageConfig(config);
      console.log('[WritingTool] Loaded platform-language config:', config.length, 'entries');
    });

    // Check if we should open a specific story (from AllStories navigation)
    const openStoryId = sessionStorage.getItem('openStoryId');
    if (openStoryId) {
      sessionStorage.removeItem('openStoryId');
      loadStory(openStoryId);
    } else {
      // Restore last-opened story across page refreshes
      const savedStoryId = localStorage.getItem('tolonews_current_story_id');
      if (savedStoryId) {
        loadStory(savedStoryId);
      }
    }
  }, []);

  // Auto-generate social posts when story reaches approved status
  // Phase 3: Changed from 'published' to 'approved'
  useEffect(() => {
    const generatePostsIfNeeded = async () => {
      // Only generate if:
      // 1. Story exists
      // 2. Status is 'approved'
      // 3. Social posts don't exist or are empty
      // 4. Not already generating
      if (
        currentStory &&
        currentStory.status === 'approved' &&
        (!currentStory.socialPosts ||
         (Array.isArray(currentStory.socialPosts?.posts) && currentStory.socialPosts.posts.length === 0) ||
         Object.keys(currentStory.socialPosts || {}).length === 0) &&
        !generatingSocialPosts
      ) {
        console.log('[Social Posts] Auto-generating posts for approved story');
        await handleGenerateSocialPosts();
      }
    };

    generatePostsIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStory?.status, currentStory?.id]); // Only depend on status and id to avoid loops

  const loadStories = async () => {
    setLoading(true);
    const groups = await getStoryGroups({ limit: 50 });
    setStoryGroups(groups);
    // Also load grid data for Phase 3 grid view
    const gridData = await getStoryGridList({ limit: 50 });
    setStoryGridList(gridData);
    setLoading(false);
  };

  const loadStory = async (id: string) => {
    setLoading(true);
    setShowVersionHistory(false);
    const story = await getSimpleStory(id);
    if (story) {
      setCurrentStory(story);
      localStorage.setItem('tolonews_current_story_id', story.id);
      // Load web article fields
      setHeadline(story.headline);
      setDeck(story.deck);
      setBody(story.body);
      // Load TV script fields from platformMetadata
      setSlug(story.platformMetadata.slug || '');
      setAnchorIntro(story.platformMetadata.anchorIntro || '');
      setScript(story.platformMetadata.script || '');
      // Load common fields
      setLanguage(story.language);
      setTvFormat(story.platformMetadata.tvFormat || DEFAULT_TV_FORMAT);

      // Load ENPS parity fields
      setScratchpad(story.scratchpad || '');
      setEditorialNotes(story.editorialNotes || '');
      // Auto-expand scratchpad if it has content
      if (story.scratchpad || story.editorialNotes) {
        setShowScratchpad(true);
      }

      // Attempt pessimistic lock (checkout)
      const checkout = await checkoutStory(story.id);
      if (checkout.success) {
        setIsCheckedOutByOther(false);
        setLockHolderName('');
        hasCheckoutLock.current = true;
      } else {
        setIsCheckedOutByOther(true);
        setLockHolderName(checkout.lockedByName || 'Another user');
        hasCheckoutLock.current = false;
      }

      // Load persisted editor issues (from AI review)
      // Show issues if they exist (regardless of status - user may have closed panel)
      if (story.editorIssues && story.editorIssues.length > 0) {
        setAiIssues(story.editorIssues as EditorIssue[]);
        // Auto-open panel for reviewed stories, otherwise just load issues (View Issues button will show)
        setShowAiPanel(story.status === 'reviewed');
      } else {
        setAiIssues([]);
        setShowAiPanel(false);
      }

      // Load editor feedback
      setEditorFeedback(story.editorFeedback || []);

      // Auto-open Editor Review Panel for editors reviewing pending_review stories
      if (story.status === 'pending_review' && isEditor && (BYPASS_AUTHOR_CHECK || story.authorId !== userId)) {
        setShowEditorReviewPanel(true);
        setShowAiPanel(false);
        setShowVersionHistory(false);
      } else {
        setShowEditorReviewPanel(false);
      }

      // Load the story group if group_id exists
      if (story.groupId) {
        const group = await getStoryGroup(story.groupId);
        setCurrentGroup(group);
      } else {
        setCurrentGroup(null);
      }

      setView('editor');
    }
    setLoading(false);
  };

  const handleSelectLanguage = async (storyId: string, language: LanguageCode) => {
    await loadStory(storyId);
  };

  const handleGroupClick = (group: StoryGroup) => {
    // If only one language, open it directly
    if (group.availableLanguages.length === 1) {
      const story = group.stories.find(s => s.language === group.availableLanguages[0]);
      if (story) {
        handleSelectLanguage(story.id, story.language);
      }
    } else {
      // Show language selector
      setSelectedGroup(group);
      setShowLanguageSelector(true);
    }
  };

  const handleCreateTranslationFromGroup = async (groupId: string, targetLang: LanguageCode) => {
    // Get the group to find a source story
    const group = await getStoryGroup(groupId);
    if (!group || group.stories.length === 0) {
      setErrorMsg('Could not find story group');
      return;
    }

    // Use canonical story as source (ensures we never translate from a translation)
    const sourceStory = getCanonicalStory(group);
    
    // Create translation using existing flow
    setShowLanguageSelector(false);
    setTranslating(true);
    setAiLoading(true);
    setAiLoadingMsg(`Translating to ${LANGUAGE_NAMES[targetLang]}...`);
    setErrorMsg(null);

    try {
      // Use TV-aware translator (handles both TV and web formats)
      const translated = await runTranslatorAgentV2(
        {
          headline: sourceStory.headline,
          deck: sourceStory.deck,
          body: sourceStory.body,
          slug: sourceStory.platformMetadata.slug,
          anchorIntro: sourceStory.platformMetadata.anchorIntro,
          script: sourceStory.platformMetadata.script,
        },
        sourceStory.language,
        targetLang
      );

      if ('error' in translated && translated.error) {
        const error = translated as AgentError;
        throw new Error(`Translation failed: ${error.message}${error.details ? ` (${error.details.slice(0, 100)})` : ''}`);
      }

      const translatedContent = translated as TranslatorResponse;

      // Validate content based on story type
      const isTvStory = sourceStory.platform === 'tv';
      const hasContent = isTvStory
        ? (translatedContent.script || translatedContent.anchor_intro)
        : translatedContent.body;

      if (!translatedContent || !hasContent) {
        throw new Error('Translation failed - no content returned');
      }

      // Create translation with both TV and web fields
      const translatedStory = await createTranslation(
        sourceStory.id,
        targetLang,
        {
          headline: translatedContent.headline || sourceStory.headline,
          deck: translatedContent.deck || sourceStory.deck,
          body: translatedContent.body || sourceStory.body,
          // Include TV fields
          slug: translatedContent.slug || sourceStory.platformMetadata.slug,
          anchorIntro: translatedContent.anchor_intro || sourceStory.platformMetadata.anchorIntro,
          script: translatedContent.script || sourceStory.platformMetadata.script,
        }
      );

      if (translatedStory) {
        // Load the new translated story
        await loadStory(translatedStory.id);
        setSuccessMsg(`Translated to ${LANGUAGE_NAMES[targetLang]}! Review and edit as needed.`);
        // Reload story groups to show updated group
        loadStories();
      } else {
        throw new Error('Failed to save translation');
      }
    } catch (error) {
      console.error('Translation error:', error);
      setErrorMsg(`Translation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setTranslating(false);
      setAiLoading(false);
      setAiLoadingMsg('');
      setTimeout(() => setSuccessMsg(null), 5000);
    }
  };

  const handleNewStory = () => {
    setCurrentStory(null);
    localStorage.removeItem('tolonews_current_story_id');
    // Reset web article fields
    setHeadline('');
    setDeck('');
    setBody('');
    // Reset TV script fields
    setSlug('');
    setAnchorIntro('');
    setScript('');
    // Reset common fields
    setLanguage(DEFAULT_LANGUAGE);
    setTvFormat(DEFAULT_TV_FORMAT);
    setAiIssues([]);
    setShowAiPanel(false);
    setShowDiff(false);
    // Reset all original content for diff tracking
    setOriginalBody('');
    setOriginalHeadline('');
    setOriginalDeck('');
    setOriginalSlug('');
    setOriginalAnchorIntro('');
    setOriginalScript('');
    // Reset ENPS parity fields
    setScratchpad('');
    setEditorialNotes('');
    setShowScratchpad(false);
    setIsCheckedOutByOther(false);
    setLockHolderName('');
    hasCheckoutLock.current = false;
    setView('editor');
  };

  const handleSave = async (): Promise<SimpleStory | null> => {
    // Validate required fields based on editor mode
    if (useTvEditor) {
      if (!slug.trim()) {
        setErrorMsg('Please enter a slug');
        return null;
      }
    } else {
      if (!headline.trim()) {
        setErrorMsg('Please enter a headline');
        return null;
      }
    }

    // Language mismatch check — block save until user confirms or switches
    const contentForLangCheck = useTvEditor ? `${anchorIntro} ${script}` : body;
    const langMismatch = detectLanguageMismatch(contentForLangCheck, language);
    if (langMismatch) {
      setLangWarningSource('save');
      setLanguageWarning(langMismatch);
      return null;
    }

    setSaving(true);
    setErrorMsg(null);

    try {
      if (currentStory) {
        // Update existing story (include language, scratchpad & editorial notes)
        const updateData = useTvEditor
          ? { language, slug, anchorIntro, script, scratchpad, editorialNotes }
          : { language, headline, deck, body, scratchpad, editorialNotes };

        const updated = await saveSimpleStory(currentStory.id, updateData, currentStory.version);
        if (updated) {
          setCurrentStory(updated);
          setLastSaved(new Date());
          setHasUnsavedChanges(false);
          setSuccessMsg('Saved successfully');
          return updated;
        }
      } else {
        // Create new story (TV platform with selected format by default)
        const created = await createSimpleStory({
          // TV script fields (for new TV stories)
          slug,
          anchorIntro,
          script,
          // Common fields
          language,
          platform: DEFAULT_PLATFORM,
          tvFormat,
        });
        if (created) {
          setCurrentStory(created);
          setLastSaved(new Date());
          setHasUnsavedChanges(false);
          setSuccessMsg('Story created');
          return created;
        }
      }
    } catch (error) {
      if (error instanceof VersionConflictError) {
        setVersionConflict(true);
        return null;
      }
      setErrorMsg('Failed to save');
      console.error(error);
    } finally {
      setSaving(false);
      setTimeout(() => setSuccessMsg(null), 3000);
    }
    return null;
  };

  const handleAiReview = async () => {
    if (currentStory?.isCanonical === false) {
      setErrorMsg('AI Review runs only on canonical stories. Derivatives inherit reviewed facts from the canonical source.');
      return;
    }

    // Check for TV content vs web content
    const hasContent = useTvEditor
      ? !!(anchorIntro.trim() || script.trim())
      : !!body.trim();

    if (!hasContent) {
      setErrorMsg(useTvEditor
        ? 'Please write some content (anchor intro or script) before running AI Review'
        : 'Please write some content before running AI Review');
      return;
    }

    // Check for invalid platform-language combination
    if (isInvalidPlatformLanguage) {
      setErrorMsg(`${LANGUAGE_NAMES[language]} is not available for TV broadcast. Please select Dari or Pashto.`);
      return;
    }

    // Check for language mismatch
    const currentLang = currentStory?.language || language;
    const contentToCheck = useTvEditor ? `${anchorIntro} ${script}` : body;
    const mismatch = detectLanguageMismatch(contentToCheck, currentLang);
    if (mismatch) {
      setLangWarningSource('aiReview');
      setLanguageWarning(mismatch);
      return; // Don't proceed until user confirms
    }

    await executeAiReview();
  };

  const executeAiReview = async () => {
    setLanguageWarning(null);

    // Save first if new story - get the saved story directly since React state won't update immediately
    let activeStory = currentStory;
    if (!activeStory) {
      const saved = await handleSave();
      if (!saved) {
        // Save failed, error already set by handleSave
        return;
      }
      activeStory = saved;
    }

    if (activeStory?.isCanonical === false) {
      setErrorMsg('AI Review runs only on canonical stories. Derivatives inherit reviewed facts from the canonical source.');
      return;
    }

    setAiLoading(true);
    setShowAiPanel(true);
    setShowVersionHistory(false);
    setAiIssues([]);
    setErrorMsg(null);

    // Store original content for diff/track changes
    setOriginalBody(body);
    setOriginalHeadline(headline);
    setOriginalDeck(deck);
    setOriginalSlug(slug);
    setOriginalAnchorIntro(anchorIntro);
    setOriginalScript(script);

    try {
      // ==========================================================================
      // AI Review: Polish content then run critics
      // ==========================================================================

      // Variables to hold polished content for critics
      let polishedHeadline = headline;
      let polishedDeck = deck;
      let polishedBody = body;
      let polishedSlug = slug;
      let polishedAnchorIntro = anchorIntro;
      let polishedScript = script;

      // Step 1: Polish content with appropriate agent
      if (useTvEditor) {
        // TV MODE: Use journalist_tv agent
        setAiLoadingMsg('Polishing TV script...');
        const polished = await runTvJournalistAgent(
          slug,
          anchorIntro,
          script,
          language,
          durationTarget,
          effectiveWordTargets,
          durationBreakdown,
          [],
          // Pass story context for fact storage
          activeStory ? {
            storyId: activeStory.id,
            groupId: activeStory.groupId || activeStory.id
          } : undefined
        );

        // Check if result is an error
        if ('error' in polished && polished.error) {
          const error = polished as AgentError;
          setErrorMsg(`AI Review failed: ${error.message}${error.details ? ` (${error.details.slice(0, 100)})` : ''}`);
          setShowAiPanel(false);
          setAiLoading(false);
          setAiLoadingMsg('');
          return;
        }

        // Update TV content with polished version
        const polishedContent = polished as TvJournalistAgentResponse;
        if (polishedContent.slug || polishedContent.anchor_intro || polishedContent.script) {
          polishedSlug = polishedContent.slug || slug;
          polishedAnchorIntro = polishedContent.anchor_intro || anchorIntro;
          polishedScript = polishedContent.script || script;
          setSlug(polishedSlug);
          setAnchorIntro(polishedAnchorIntro);
          setScript(polishedScript);

          // Save polished content to database immediately
          if (activeStory) {
            await saveSimpleStory(activeStory.id, {
              slug: polishedSlug,
              anchorIntro: polishedAnchorIntro,
              script: polishedScript,
            });
            console.log('[AIReview] Polished TV content saved to database');
          }
        }
      } else {
        // WEB MODE: Use standard journalist agent
        setAiLoadingMsg('Polishing your writing...');
        const polished = await runJournalistAgent(headline, deck, body, language, []);

        // Check if result is an error
        if ('error' in polished && polished.error) {
          const error = polished as AgentError;
          setErrorMsg(`AI Review failed: ${error.message}${error.details ? ` (${error.details.slice(0, 100)})` : ''}`);
          setShowAiPanel(false);
          setAiLoading(false);
          setAiLoadingMsg('');
          return;
        }

        // Update web content with polished version
        const polishedContent = polished as { headline?: string; deck?: string; body?: string };
        if (polishedContent.headline || polishedContent.deck || polishedContent.body) {
          polishedHeadline = polishedContent.headline || headline;
          polishedDeck = polishedContent.deck || deck;
          polishedBody = polishedContent.body || body;
          setHeadline(polishedHeadline);
          setDeck(polishedDeck);
          setBody(polishedBody);

          // Save polished content to database immediately
          if (activeStory) {
            await saveSimpleStory(activeStory.id, {
              headline: polishedHeadline,
              deck: polishedDeck,
              body: polishedBody,
            });
            console.log('[AIReview] Polished web content saved to database');
          }
        }
      }

      // Step 1.5: Fetch facts for verification (TV stories only, full mode)
        // Facts are only available for TV stories that have been polished before
        let factsToVerify: FactForVerification[] = [];
        const isLightMode = reviewMode === 'light';

        if (!isLightMode && activeStory?.groupId && useTvEditor) {
          try {
            setAiLoadingMsg('Loading facts for verification...');
            const facts = await getFactsForGroup(activeStory.groupId);
            // Only verify facts that haven't been verified yet
            factsToVerify = facts
              .filter(f => f.confidence === 'attributed' || f.confidence === 'unverified')
              .map(f => ({
                id: f.id,
                content: f.content,
                fact_type: f.fact_type,
                source_attribution: f.source_attribution || undefined,
                confidence: f.confidence,
              }));
            console.log(`[AIReview] Fetched ${factsToVerify.length} facts for verification (from ${facts.length} total)`);
          } catch (e) {
            console.warn('[AIReview] Failed to fetch facts for verification:', e);
            // Continue without facts - not a blocking error
          }
        }

        // Step 2: Run critics based on review mode
        // - 'full': All 4 critics (canonical content)
        // - 'light': Only style critic (derivatives)
        setAiLoadingMsg(isLightMode
          ? 'Running style check...'
          : factsToVerify.length > 0
            ? `Running 4 critics in parallel (${factsToVerify.length} facts to verify)...`
            : 'Running 4 critics in parallel...');

        // For critics, we pass the content that will be reviewed
        // TV content: slug is editorial-only, so pass empty headline; critics review anchor_intro + script only
        const contentForCritics = useTvEditor
          ? {
              headline: '',
              deck: polishedAnchorIntro,
              body: polishedScript,
            }
          : {
              headline: polishedHeadline,
              deck: polishedDeck,
              body: polishedBody,
            };

        const criticsResult = await runParallelCritics(
          contentForCritics.headline,
          contentForCritics.deck,
          contentForCritics.body,
          language,
          reviewMode,
          factsToVerify.length > 0 ? factsToVerify : undefined
        );

        // Collect issues from all successful critics
        let allIssues: EditorIssue[] = [
          ...criticsResult.style.issues,
          ...criticsResult.journalism.issues,
          ...criticsResult.factCheck.issues,
          ...criticsResult.deepCheck.issues,
        ];

        // Show warnings for any failures (only for full mode)
        if (!isLightMode) {
          const failures: string[] = [];
          if (!criticsResult.style.success) failures.push('style');
          if (!criticsResult.journalism.success) failures.push('editorial');
          if (!criticsResult.factCheck.success) failures.push('fact-check');
          if (!criticsResult.deepCheck.success) failures.push('deep-search');

          if (failures.length > 0 && allIssues.length > 0) {
            setSuccessMsg(`Some checks skipped (${failures.join(', ')}) - showing available results`);
          } else if (failures.length === 4) {
            setErrorMsg('All critics failed - please try again');
          }
        }

        console.log(`[ParallelCritics] ${isLightMode ? 'LIGHT' : 'FULL'} mode completed in ${criticsResult.duration_ms}ms, collected ${allIssues.length} issues`);

        // Check if any facts were updated by critics and refresh the FactsPanel
        const totalFactsUpdated =
          (criticsResult.factCheck.factsUpdateCount || 0) +
          (criticsResult.deepCheck.factsUpdateCount || 0);
        if (totalFactsUpdated > 0) {
          console.log(`[ParallelCritics] ${totalFactsUpdated} fact confidences updated - refreshing FactsPanel`);
          setFactsRefreshKey(prev => prev + 1);
        }

        // Step 2.5: Promote uncontested attributed facts to verified (canonical TV full review only)
        const isCanonicalStory = !!activeStory && activeStory.isCanonical;
        const shouldRunConsensusPromotion =
          useTvEditor &&
          !isLightMode &&
          isCanonicalStory &&
          !!activeStory?.groupId &&
          factsToVerify.length > 0;

        if (shouldRunConsensusPromotion && activeStory) {
          try {
            const promotionResult = await runFactConsensusPromotion(
              {
                storyId: activeStory.id,
                groupId: activeStory.groupId || activeStory.id,
              },
              language,
              factsToVerify.map((f) => f.id),
              {
                factCheckerSuccess: criticsResult.factCheck.success,
                deepCheckSuccess: criticsResult.deepCheck.success,
                factCheckerParsed: criticsResult.factCheck.responseParsed === true,
                deepCheckParsed: criticsResult.deepCheck.responseParsed === true,
              }
            );

            if (isAgentError(promotionResult)) {
              console.warn('[AIReview] fact_consensus_promotion failed (non-blocking):', promotionResult.message);
            } else {
              console.log(
                `[AIReview] fact_consensus_promotion: promoted=${promotionResult.promoted_count}, eligible=${promotionResult.eligible_count}, candidates=${promotionResult.candidate_count}${promotionResult.skipped_reason ? `, skipped_reason=${promotionResult.skipped_reason}` : ''}`
              );
              if (promotionResult.promoted_count > 0) {
                setFactsRefreshKey(prev => prev + 1);
              }
            }
          } catch (promotionError) {
            console.warn('[AIReview] fact_consensus_promotion exception (non-blocking):', promotionError);
          }
        }

        // Step 3: Curator (Phase 2e - DeepSeek V3)
        // Consolidates, deduplicates, and prioritizes all issues
        // Skip curator in light mode (not worth the cost for few style issues)
        // Track final issues for persistence
        let finalIssues: EditorIssue[] = allIssues;

        if (allIssues.length > 0 && !isLightMode) {
          setAiLoadingMsg('Prioritizing issues...');
          const curatedRaw = await runCuratorAgent(allIssues, language);

          if (isAgentError(curatedRaw)) {
            console.error(`Curator failed: ${curatedRaw.message}`);
            // Graceful degradation: show original unmerged issues
            setAiIssues(allIssues);
            setSuccessMsg('Issue prioritization skipped - showing all issues');
          } else {
            const curator = parseAgentResponse<CuratorResponse>(curatedRaw);
            if (curator?.issues && curator.issues.length > 0) {
              // Use curated issues (top 10, deduplicated, prioritized)
              finalIssues = curator.issues as EditorIssue[];
              setAiIssues(finalIssues);
              console.log(`[Curator] ${curator.total_input_count} → ${curator.issues.length} issues (merged ${curator.merged_count}${curator.reconciled_count ? `, reconciled ${curator.reconciled_count}` : ''})`);
            } else {
              // Fallback if parsing failed
              setAiIssues(allIssues);
              console.warn('[Curator] Failed to parse curated issues, showing all');
            }
          }
        } else {
          // Light mode or no issues - just show what we have
          setAiIssues(allIssues);
        }

        // Update status (Phase 3: use 'reviewed' status) and persist issues
        if (activeStory) {
          await setReviewedStatus(activeStory.id);
          // Persist issues to database so they survive page reloads
          await saveSimpleStory(activeStory.id, { editorIssues: finalIssues });
          setCurrentStory({ ...activeStory, status: 'reviewed', editorIssues: finalIssues });
        }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setErrorMsg(`AI Review failed: ${errorMessage}`);
      console.error('AI Review exception:', error);
      setShowAiPanel(false);
    } finally {
      setAiLoading(false);
      setAiLoadingMsg('');
    }
  };

  const handleApplyFixes = async () => {
    const fixableIssues = aiIssues.filter(
      (i) => i.category === 'ai_fixable' && !i.applied
    );

    if (fixableIssues.length === 0) {
      setErrorMsg('No AI-fixable issues to apply');
      return;
    }

    setAiLoading(true);
    setAiLoadingMsg('Applying fixes...');
    setErrorMsg(null);

    try {
      // Format feedback with original/suggestion context for better fixes
      const feedbackItems = fixableIssues.map((i) => {
        if (i.original && i.suggestion) {
          return `${i.text} (Change "${i.original}" to "${i.suggestion}")`;
        }
        return i.suggestion ? `${i.text} (Suggestion: ${i.suggestion})` : i.text;
      });

      if (useTvEditor) {
        // TV MODE: Use journalist_tv agent with feedback
        const polished = await runTvJournalistAgent(
          slug,
          anchorIntro,
          script,
          language,
          durationTarget,
          effectiveWordTargets,
          durationBreakdown,
          feedbackItems,
          // Pass story context for fact storage
          currentStory ? {
            storyId: currentStory.id,
            groupId: currentStory.groupId || currentStory.id
          } : undefined
        );

        // Check if result is an error
        if ('error' in polished && polished.error) {
          const error = polished as AgentError;
          setErrorMsg(`Failed to apply fixes: ${error.message}${error.details ? ` (${error.details.slice(0, 100)})` : ''}`);
          return;
        }

        // Update TV content with polished version
        const polishedContent = polished as TvJournalistAgentResponse;
        if (polishedContent.slug || polishedContent.anchor_intro || polishedContent.script) {
          setSlug(polishedContent.slug || slug);
          setAnchorIntro(polishedContent.anchor_intro || anchorIntro);
          setScript(polishedContent.script || script);
        }
      } else {
        // WEB MODE: Use standard journalist agent
        const polished = await runJournalistAgent(
          headline,
          deck,
          body,
          language,
          feedbackItems
        );

        // Check if result is an error
        if ('error' in polished && polished.error) {
          const error = polished as AgentError;
          setErrorMsg(`Failed to apply fixes: ${error.message}${error.details ? ` (${error.details.slice(0, 100)})` : ''}`);
          return;
        }

        // Update web content with polished version
        const polishedContent = polished as { headline?: string; deck?: string; body?: string };
        if (polishedContent.headline || polishedContent.deck || polishedContent.body) {
          setHeadline(polishedContent.headline || headline);
          setDeck(polishedContent.deck || deck);
          setBody(polishedContent.body || body);
        }
      }

      // Mark applied issues with status instead of removing
      setAiIssues((prev) =>
        prev.map((i) => i.category === 'ai_fixable' && (!i.status || i.status === 'pending')
          ? { ...i, status: 'applied' as const, appliedAt: new Date().toISOString() }
          : i
        )
      );

      setSuccessMsg(`Applied ${fixableIssues.length} fix(es)`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setErrorMsg(`Failed to apply fixes: ${errorMessage}`);
      console.error('Apply fixes exception:', error);
    } finally {
      setAiLoading(false);
      setAiLoadingMsg('');
      setTimeout(() => setSuccessMsg(null), 3000);
    }
  };

  const handleArchive = async () => {
    if (!currentStory) return;

    if (!window.confirm('Archive this story? It will be hidden from the list.')) {
      return;
    }

    const success = await archiveSimpleStory(currentStory.id);
    if (success) {
      setSuccessMsg('Story archived');
      setView('list');
      loadStories();
    } else {
      setErrorMsg('Failed to archive');
    }
  };

  // ==========================================================================
  // Phase 3: Unlock approved story
  // ==========================================================================
  const handleUnlock = async () => {
    if (!currentStory) return;

    // Check if this is a canonical story with derivatives
    if (currentStory.isCanonical) {
      const derivatives = await getStoryDerivatives(currentStory.id);
      if (derivatives.length > 0) {
        setShowUnlockWarning(true);
        return;
      }
    }

    // No derivatives, unlock directly
    await performUnlock();
  };

  const performUnlock = async () => {
    if (!currentStory) return;

    setUnlocking(true);
    setShowUnlockWarning(false);

    const success = await unlockStory(currentStory.id);
    if (success) {
      // Reload story to get updated status
      const updated = await getSimpleStory(currentStory.id);
      if (updated) {
        setCurrentStory(updated);
      }
      // Clear local issues state so AI Review button reappears (DB already cleared by unlockStory)
      setAiIssues([]);
      setShowAiPanel(false);
      setSuccessMsg('Story unlocked for editing');
    } else {
      setErrorMsg('Failed to unlock story');
    }

    setUnlocking(false);
  };

  // ==========================================================================
  // ==========================================================================
  // Editor Workflow: Submit for Review (journalist action)
  // ==========================================================================
  const handleSubmitForReview = async () => {
    if (!currentStory) return;

    setSubmittingForReview(true);
    try {
      // Save first
      await handleSave();

      const success = await submitForReview(currentStory.id);
      if (success) {
        const updated = await getSimpleStory(currentStory.id);
        if (updated) setCurrentStory(updated);
        setSuccessMsg('Story submitted for editor review');
      } else {
        setErrorMsg('Failed to submit for review');
      }
    } catch (err) {
      console.error('[handleSubmitForReview] Error:', err);
      setErrorMsg('Failed to submit for review');
    } finally {
      setSubmittingForReview(false);
    }
  };

  // ==========================================================================
  // Editor Workflow: Editor Approve (editor action on pending_review)
  // ==========================================================================
  const handleEditorApprove = async () => {
    if (!currentStory) return;

    // Save first (editor may have made changes)
    await handleSave();

    // Use editorApproveStory which sets approved + clears issues
    const success = await editorApproveStory(currentStory.id);
    if (!success) {
      setErrorMsg('Failed to approve story');
      return;
    }

    // Clear local issues and feedback state
    setAiIssues([]);
    setShowAiPanel(false);
    setEditorFeedback([]);
    setShowEditorReviewPanel(false);

    // Reload story to get updated status
    const updated = await getSimpleStory(currentStory.id);
    if (updated) setCurrentStory(updated);

    // Auto-create derivative drafts (same logic as handleApprove)
    const approvedStory = updated || currentStory;
    try {
      const { created, existing, skipped, errors } = await createAllDerivativeDrafts(approvedStory);
      console.log('[handleEditorApprove] createAllDerivativeDrafts result:', { created: created.length, existing: existing.length, skipped, errors });

      await loadStories();

      // Combine new + existing derivatives for content generation
      const allDerivatives = [...created, ...existing];

      if (allDerivatives.length > 0) {
        const label = created.length > 0 && existing.length > 0
          ? `Story approved. ${created.length} new + ${existing.length} existing derivatives queued for generation...`
          : created.length > 0
            ? `Story approved. ${created.length} derivatives queued for generation...`
            : `Story approved. Re-generating ${existing.length} existing derivatives...`;
        setSuccessMsg(label);
        generateAllDerivativeContent(approvedStory, allDerivatives);
      } else if (errors > 0) {
        setSuccessMsg('Story approved and locked');
        setErrorMsg(`Warning: ${errors} derivative draft(s) could not be created`);
      } else {
        setSuccessMsg('Story approved and locked.');
      }
    } catch (err) {
      console.error('[handleEditorApprove] Error creating derivatives:', err);
      setErrorMsg(`Story approved but derivative creation failed: ${err instanceof Error ? err.message : String(err)}`);
      await loadStories();
    }
  };

  // ==========================================================================
  // Editor Workflow: Editor Reject / Send Back (editor action on pending_review)
  // ==========================================================================
  const handleEditorReject = async (notes?: string) => {
    if (!currentStory) return;

    // Accept notes from param (EditorReviewPanel) or state (legacy dialog)
    let rejectionText = notes || rejectNotes.trim();

    // If no notes but feedback cards exist, auto-generate
    if (!rejectionText && editorFeedback.length > 0) {
      rejectionText = editorFeedback.map((card, i) => {
        const severity = card.severity ? `[${card.severity.replace('_', ' ')}]` : '';
        const loc = card.location ? `(${card.location})` : '';
        return `${i + 1}. ${severity} ${loc} ${card.text}`;
      }).join('\n');
    }

    if (!rejectionText) return;

    try {
      const success = await editorRejectStory(currentStory.id, rejectionText, editorFeedback.length > 0 ? editorFeedback : undefined);
      if (success) {
        const updated = await getSimpleStory(currentStory.id);
        if (updated) setCurrentStory(updated);
        setSuccessMsg('Story sent back to journalist with feedback');
        setShowRejectDialog(false);
        setShowEditorReviewPanel(false);
        setRejectNotes('');
      } else {
        setErrorMsg('Failed to send story back');
      }
    } catch (err) {
      console.error('[handleEditorReject] Error:', err);
      setErrorMsg('Failed to send story back');
    }
  };

  // ==========================================================================
  // Editor Workflow: Capture text selection from editor fields
  // ==========================================================================
  // Track selection proactively via onSelect events on textareas/inputs.
  // By the time "+ Add" click fires, the browser has already moved focus away
  // from the textarea, so reading document.activeElement at click time fails.
  // Instead, each field's onSelect writes to lastTextSelectionRef, and
  // getEditorTextSelection simply reads from it.
  const handleEditorSelect = useCallback((field: EditorFeedbackCard['location']) => {
    const active = document.activeElement;
    if (active instanceof HTMLTextAreaElement || active instanceof HTMLInputElement) {
      const start = active.selectionStart;
      const end = active.selectionEnd;
      if (start != null && end != null && start !== end) {
        const selectedText = active.value.substring(start, end).trim();
        if (selectedText) {
          lastTextSelectionRef.current = { text: selectedText, location: field };
          return;
        }
      }
    }
    lastTextSelectionRef.current = null;
  }, []);

  const getEditorTextSelection = useCallback(() => {
    return lastTextSelectionRef.current;
  }, []);

  // ==========================================================================
  // Editor Workflow: Feedback card handlers
  // ==========================================================================
  const handleAddEditorFeedback = async (card: Omit<EditorFeedbackCard, 'id' | 'createdAt' | 'createdBy' | 'createdByName'>) => {
    if (!currentStory) return;
    const newCard: EditorFeedbackCard = {
      ...card,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      createdBy: userId || '',
      createdByName: fullName || undefined,
    };
    const updated = [...editorFeedback, newCard];
    setEditorFeedback(updated);
    await saveSimpleStory(currentStory.id, { editorFeedback: updated });
    setCurrentStory({ ...currentStory, editorFeedback: updated });
  };

  const handleRemoveEditorFeedback = async (id: string) => {
    if (!currentStory) return;
    const updated = editorFeedback.filter(c => c.id !== id);
    setEditorFeedback(updated);
    await saveSimpleStory(currentStory.id, { editorFeedback: updated });
    setCurrentStory({ ...currentStory, editorFeedback: updated });
  };

  // ==========================================================================
  // Phase 3: Auto-generate all derivative content (background, fire-and-forget)
  // ==========================================================================
  const generateAllDerivativeContent = async (
    canonicalStory: SimpleStory,
    derivatives: SimpleStory[]
  ) => {
    const groupId = canonicalStory.groupId;
    if (!groupId) return;

    let completed = 0;
    let failed = 0;
    const total = derivatives.length;

    // Mark all cells as generating
    setGeneratingCells(prev => [
      ...prev,
      ...derivatives.map(d => ({ groupId: groupId, platform: d.platform, language: d.language })),
    ]);

    // Helper to generate content for a single derivative and update the draft row
    const processOne = async (derivative: SimpleStory) => {
      try {
        const sourceIsTv = canonicalStory.platform === 'tv';
        const targetIsWeb = derivative.platform === 'web';
        const targetIsSocial = derivative.platform === 'social';
        const needsTranslation = derivative.language !== canonicalStory.language;
        let content: Record<string, string> = {};
        let socialPostsResult: any = null;

        if (sourceIsTv && targetIsWeb && !needsTranslation) {
          // TV -> Web (same language): web_rewriter with Facts Stream
          const result = await runWebRewriterAgent(
            canonicalStory.platformMetadata.slug || '',
            canonicalStory.platformMetadata.anchorIntro || '',
            canonicalStory.platformMetadata.script || '',
            canonicalStory.language,
            groupId
          );
          if (isAgentError(result)) throw new Error(result.message);
          content = { headline: result.headline, deck: result.deck, body: result.body };

        } else if (sourceIsTv && targetIsWeb && needsTranslation) {
          // TV -> Web + Translation: web_rewriter then translator
          const webResult = await runWebRewriterAgent(
            canonicalStory.platformMetadata.slug || '',
            canonicalStory.platformMetadata.anchorIntro || '',
            canonicalStory.platformMetadata.script || '',
            canonicalStory.language,
            groupId
          );
          if (isAgentError(webResult)) throw new Error(webResult.message);

          const transResult = await runTranslatorAgentV2({
            headline: webResult.headline,
            deck: webResult.deck,
            body: webResult.body,
          }, canonicalStory.language, derivative.language);
          if (isAgentError(transResult)) throw new Error(transResult.message);
          content = {
            headline: transResult.headline || '',
            deck: transResult.deck || '',
            body: transResult.body || '',
          };

        } else if (targetIsSocial) {
          // Social: use social_generator
          // For cross-language social, prefer the same-language web article (richer than TV script)
          // For same-language social, use canonical TV content directly
          let sourceContent: Record<string, string | undefined> = {
            headline: canonicalStory.headline,
            deck: canonicalStory.deck,
            body: canonicalStory.body,
            slug: canonicalStory.platformMetadata.slug,
            anchorIntro: canonicalStory.platformMetadata.anchorIntro,
            script: canonicalStory.platformMetadata.script,
          };

          // If social language differs from canonical, look up the web article in that language
          if (derivative.language !== canonicalStory.language && groupId) {
            const webArticle = await getExistingDerivative(groupId, 'web', derivative.language);
            if (webArticle && webArticle.headline) {
              sourceContent = {
                headline: webArticle.headline,
                deck: webArticle.deck,
                body: webArticle.body,
              };
            }
          }

          const result = await runSocialGeneratorAgent({
            headline: sourceContent.headline,
            deck: sourceContent.deck,
            body: sourceContent.body,
            slug: sourceContent.slug,
            anchorIntro: sourceContent.anchorIntro,
            script: sourceContent.script,
          }, derivative.language, groupId);
          if (isAgentError(result)) throw new Error(result.message);
          // Store clean summary in body; actual posts go in socialPosts field
          const postCount = result?.posts?.length || 0;
          content = {
            headline: `Social posts for: ${canonicalStory.platformMetadata.slug || canonicalStory.headline}`,
            deck: `${postCount} social media posts generated`,
            body: '',
          };
          // Save socialPosts separately after the draft update below
          socialPostsResult = result;

        } else if (needsTranslation) {
          // TV translation (e.g., Dari TV -> Pashto TV)
          const result = await runTranslatorAgentV2({
            headline: canonicalStory.headline,
            deck: canonicalStory.deck,
            body: canonicalStory.body,
            slug: canonicalStory.platformMetadata.slug,
            anchorIntro: canonicalStory.platformMetadata.anchorIntro,
            script: canonicalStory.platformMetadata.script,
          }, canonicalStory.language, derivative.language);
          if (isAgentError(result)) throw new Error(result.message);
          content = {
            headline: result.headline || '',
            deck: result.deck || '',
            body: result.body || '',
            slug: result.slug || canonicalStory.platformMetadata.slug || '',
            anchorIntro: result.anchor_intro || '',
            script: result.script || '',
          };
        }

        // Update the empty draft with generated content
        await saveSimpleStory(derivative.id, {
          headline: content.headline || '',
          deck: content.deck || '',
          body: content.body || '',
          slug: content.slug,
          anchorIntro: content.anchorIntro,
          script: content.script,
          originalHeadline: content.headline || '',
          originalDeck: content.deck || '',
          originalBody: content.body || '',
          ...(socialPostsResult ? { socialPosts: socialPostsResult } : {}),
        });

        completed++;
        setSuccessMsg(`Generated ${completed}/${total} derivatives...`);
      } catch (err) {
        failed++;
        console.error(`[Auto-gen] Failed ${derivative.platform}/${derivative.language}:`, err);
      } finally {
        // Remove this cell from generating state
        setGeneratingCells(prev =>
          prev.filter(c => !(c.platform === derivative.platform && c.language === derivative.language && c.groupId === groupId))
        );
      }
    };

    // Phase 1: Web derivatives (parallel — each independently derives from canonical TV + facts)
    const webDerivatives = derivatives.filter(d => d.platform === 'web');
    const tvDerivatives = derivatives.filter(d => d.platform === 'tv');
    const socialDerivatives = derivatives.filter(d => d.platform === 'social');

    await Promise.allSettled(webDerivatives.map(d => processOne(d)));

    // Phase 2: TV translations + social posts (parallel, after Phase 1 so English social has web content)
    await Promise.allSettled([
      ...tvDerivatives.map(d => processOne(d)),
      ...socialDerivatives.map(d => processOne(d)),
    ]);

    // Final grid refresh
    await loadStories();

    if (failed > 0) {
      setErrorMsg(`${failed} derivative(s) failed to generate. Click Pending cells to retry.`);
    } else if (completed > 0) {
      setSuccessMsg(`All ${completed} derivatives generated successfully.`);
    }
  };

  // ==========================================================================
  // Phase 3: Generate derivative (web article, translation, social)
  // ==========================================================================
  const handleGenerateDerivative = async (targetPlatform: Platform, targetLanguage: LanguageCode, sourceStory?: SimpleStory) => {
    const story = sourceStory || currentStory;
    if (!story) return;

    // Block if a derivative is already being generated
    if (generatingDerivative) {
      setErrorMsg('A derivative is already being generated. Please wait for it to finish.');
      return;
    }

    // Must be approved before generating derivatives
    if (story.status !== 'approved') {
      setErrorMsg('Story must be approved before generating derivatives');
      return;
    }

    setDerivativeTarget({ platform: targetPlatform, language: targetLanguage, sourceStory: story });
    setShowDerivativeModal(true);
  };

  const performDerivativeGeneration = async () => {
    if (!derivativeTarget) return;
    const story = derivativeTarget.sourceStory || currentStory;
    if (!story) return;

    // Synchronous ref guard prevents double-submit before React state update flushes
    if (isGeneratingDerivativeRef.current) return;
    isGeneratingDerivativeRef.current = true;

    const launchedFromList = view === 'list';
    setGeneratingDerivative(true);
    setShowDerivativeModal(false);

    const { platform: targetPlatform, language: targetLanguage } = derivativeTarget;

    // Show generating indicator on the grid cell
    if (launchedFromList && story.groupId) {
      setGeneratingCells(prev => [...prev, { groupId: story.groupId!, platform: targetPlatform, language: targetLanguage }]);
    }

    try {
      let content: Record<string, string> = {};
      let manualSocialPostsResult: any = null;

      // Determine which agent to call based on target
      const sourceIsTv = story.platform === 'tv';
      const targetIsWeb = targetPlatform === 'web';
      const targetIsSocial = targetPlatform === 'social';
      const needsTranslation = targetLanguage !== story.language;

      setAiLoadingMsg('Generating derivative...');

      if (sourceIsTv && targetIsWeb && !needsTranslation) {
        // TV -> Web (same language): use web_rewriter with Facts Stream
        const result = await runWebRewriterAgent(
          story.platformMetadata.slug || '',
          story.platformMetadata.anchorIntro || '',
          story.platformMetadata.script || '',
          story.language,
          story.groupId  // Pass group ID for facts loading
        );
        if (isAgentError(result)) throw new Error(result.message);
        content = {
          headline: result.headline,
          deck: result.deck,
          body: result.body,
        };
      } else if (sourceIsTv && targetIsWeb && needsTranslation) {
        // TV -> Web + Translation (e.g., English): web_rewriter then translator
        // Uses Facts Stream for richer content than just translating TV script
        setAiLoadingMsg('Generating web article from facts...');
        const webResult = await runWebRewriterAgent(
          story.platformMetadata.slug || '',
          story.platformMetadata.anchorIntro || '',
          story.platformMetadata.script || '',
          story.language,
          story.groupId  // Pass group ID for facts loading
        );
        if (isAgentError(webResult)) throw new Error(webResult.message);

        // Then translate to target language
        setAiLoadingMsg('Translating to target language...');
        const transResult = await runTranslatorAgentV2({
          headline: webResult.headline,
          deck: webResult.deck,
          body: webResult.body,
        }, story.language, targetLanguage);
        if (isAgentError(transResult)) throw new Error(transResult.message);
        content = {
          headline: transResult.headline || '',
          deck: transResult.deck || '',
          body: transResult.body || '',
        };
      } else if (targetIsSocial) {
        // Any -> Social: use social_generator
        const result = await runSocialGeneratorAgent({
          headline: story.headline,
          deck: story.deck,
          body: story.body,
          slug: story.platformMetadata.slug,
          anchorIntro: story.platformMetadata.anchorIntro,
          script: story.platformMetadata.script,
        }, targetLanguage);
        if (isAgentError(result)) throw new Error(result.message);
        // Store clean summary in body; actual posts go in socialPosts field
        const postCount = result?.posts?.length || 0;
        content = {
          headline: `Social posts for: ${story.platformMetadata.slug || story.headline}`,
          deck: `${postCount} social media posts generated`,
          body: '',
        };
        manualSocialPostsResult = result;
      } else if (needsTranslation) {
        // Non-TV translation: use translator directly
        const result = await runTranslatorAgentV2({
          headline: story.headline,
          deck: story.deck,
          body: story.body,
          slug: story.platformMetadata.slug,
          anchorIntro: story.platformMetadata.anchorIntro,
          script: story.platformMetadata.script,
        }, story.language, targetLanguage);
        if (isAgentError(result)) throw new Error(result.message);
        content = {
          headline: result.headline || '',
          deck: result.deck || '',
          body: result.body || '',
          slug: result.slug || story.platformMetadata.slug || '',
          anchorIntro: result.anchor_intro || '',
          script: result.script || '',
        };
      }

      // Check if an empty draft already exists (e.g., from auto-creation on approve)
      let derivative: SimpleStory | null = null;
      if (story.groupId) {
        const existing = await getExistingDerivative(story.groupId, targetPlatform, targetLanguage);
        if (existing) {
          const isEmpty = !existing.headline && !existing.body && !existing.platformMetadata.script;
          if (isEmpty) {
            // Update existing empty draft with generated content
            derivative = await saveSimpleStory(existing.id, {
              headline: content.headline || '',
              deck: content.deck || '',
              body: content.body || '',
              slug: content.slug,
              anchorIntro: content.anchorIntro,
              script: content.script,
              originalHeadline: content.headline || '',
              originalDeck: content.deck || '',
              originalBody: content.body || '',
              ...(manualSocialPostsResult ? { socialPosts: manualSocialPostsResult } : {}),
            });
          } else {
            // Derivative with content already exists
            setErrorMsg('This derivative already exists');
            return;
          }
        }
      }

      // If no existing draft was found, create a new one
      if (!derivative) {
        derivative = await createDerivativeStory(
          story.id,
          targetPlatform,
          targetLanguage,
          content
        );
      }

      // Save socialPosts separately if this was a social derivative created via createDerivativeStory
      if (derivative && manualSocialPostsResult) {
        await saveSimpleStory(derivative.id, { socialPosts: manualSocialPostsResult });
      }

      if (derivative) {
        // Auto-generate English social posts after English web creation
        // English has no TV, so English social derives from English web (which uses Facts Stream)
        if (targetPlatform === 'web' && targetLanguage === 'eng') {
          setAiLoadingMsg('Generating English social posts...');
          try {
            const socialResult = await runSocialGeneratorAgent({
              headline: content.headline,
              deck: content.deck,
              body: content.body,
            }, 'eng', story.groupId);  // Pass groupId for verified facts

            if (!isAgentError(socialResult)) {
              await saveSimpleStory(derivative.id, { socialPosts: socialResult });
              console.log('[English Web] Auto-generated social posts');
            }
          } catch (socialError) {
            console.warn('English social auto-generation failed:', socialError);
            // Non-blocking - web article still created successfully
          }
        }

        // Reload stories list so grid reflects the new derivative
        await loadStories();

        if (launchedFromList) {
          // Stay on list view — journalist can keep working
          setSuccessMsg(
            targetPlatform === 'web' && targetLanguage === 'eng'
              ? 'English web article and social posts created'
              : 'Derivative created successfully'
          );
        } else {
          // Open the new derivative in editor
          await loadStory(derivative.id);
          setView('editor');
          setSuccessMsg(
            targetPlatform === 'web' && targetLanguage === 'eng'
              ? 'English web article and social posts created'
              : 'Derivative created successfully'
          );
        }
      } else {
        throw new Error('Failed to create derivative story');
      }
    } catch (error) {
      console.error('Derivative generation failed:', error);
      setErrorMsg(`Failed to generate derivative: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      isGeneratingDerivativeRef.current = false;
      setGeneratingDerivative(false);
      setDerivativeTarget(null);
      // Remove this cell from generating list
      if (derivativeTarget) {
        const { platform: tp, language: tl } = derivativeTarget;
        setGeneratingCells(prev => prev.filter(c => !(c.platform === tp && c.language === tl)));
      }
      setAiLoadingMsg('');
    }
  };

  // ==========================================================================
  // Phase 3: Grid cell click handlers
  // ==========================================================================
  const handleGridCellClick = async (platform: Platform, language: LanguageCode, story: SimpleStory | null) => {
    if (story) {
      // Story exists, open it
      await loadStory(story.id);
      setView('editor');
    }
    // If no story, handleGridGenerateClick handles generation
  };

  const handleGridGenerateClick = (platform: Platform, language: LanguageCode) => {
    // Find the canonical story for this group
    // This assumes we're looking at a specific grid
    // For now, open derivative modal with the target
    handleGenerateDerivative(platform, language);
  };

  const handleGenerateSocialPosts = async () => {
    if (!currentStory) return;

    setGeneratingSocialPosts(true);
    setSocialPostsError(null);
    setErrorMsg(null);

    try {
      // Save story first to ensure latest content is saved
      await handleSave();
      
      // Reload story to get latest version
      const latestStory = await getSimpleStory(currentStory.id);
      if (!latestStory) {
        throw new Error('Could not load story');
      }

      // Story must be approved by an editor before social posts can be generated
      if (latestStory.status !== 'approved') {
        throw new Error('Story must be approved by an editor before generating social posts');
      }

      const storyToUse = currentStory.status === 'approved' ? currentStory : await getSimpleStory(currentStory.id);
      if (!storyToUse) {
        throw new Error('Could not load story');
      }

      const result = await runSocialGeneratorAgent(
        {
          headline: headline || storyToUse.headline,
          deck: storyToUse.deck,
          body: body || storyToUse.body,
          slug: storyToUse.platformMetadata.slug,
          anchorIntro: anchorIntro || storyToUse.platformMetadata.anchorIntro,
          script: script || storyToUse.platformMetadata.script,
        },
        storyToUse.language,
        storyToUse.groupId  // Pass groupId for verified facts
      );

      // Check if result is an error
      if ('error' in result && result.error) {
        const error = result as AgentError;
        const errorMsg = `Failed to generate social posts: ${error.message}${error.details ? ` (${error.details.slice(0, 100)})` : ''}`;
        setSocialPostsError(errorMsg);
        setErrorMsg(errorMsg);
        return;
      }

      const posts = result as { posts?: any[] };
      if (!posts || !posts.posts || posts.posts.length === 0) {
        throw new Error('No posts generated');
      }

      // Save posts to story
      const updated = await saveSimpleStory(storyToUse.id, {
        socialPosts: posts
      });

      if (updated) {
        setCurrentStory(updated);
        setSuccessMsg('Social media posts generated! Story has been published.');
        setTimeout(() => setSuccessMsg(null), 5000);
      } else {
        throw new Error('Failed to save social posts');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorMsg = `Failed to generate social posts: ${errorMessage}`;
      setSocialPostsError(errorMsg);
      setErrorMsg(errorMsg);
      console.error('Social posts generation error:', error);
    } finally {
      setGeneratingSocialPosts(false);
    }
  };

  // Get available target languages (only show languages that haven't been translated yet)
  const getAvailableTranslationLanguages = async (): Promise<LanguageCode[]> => {
    const currentLang = currentStory?.language || language;
    const currentPlatform = currentStory?.platform || 'tv';

    // Filter out languages not allowed for this platform (e.g., English TV)
    const config = await getPlatformLanguageConfig();
    const platformAllowed = (lang: LanguageCode) => {
      const entry = config.find(c => c.platform === currentPlatform && c.language === lang);
      return entry ? entry.allowed : true;
    };

    // If no current story or no group, return all except current (filtered by platform)
    if (!currentStory || !currentStory.groupId) {
      return SUPPORTED_LANGUAGES.filter((lang) => lang !== currentLang && platformAllowed(lang));
    }

    // Get the group to see what's already translated
    const group = await getStoryGroup(currentStory.groupId);
    if (!group) {
      return SUPPORTED_LANGUAGES.filter((lang) => lang !== currentLang && platformAllowed(lang));
    }

    // Return only languages that don't exist in the group AND are allowed for this platform
    return group.missingLanguages.filter(platformAllowed);
  };

  const handleTranslate = async (targetLang: LanguageCode) => {
    setShowTranslateModal(false);
    setTranslating(true);
    setAiLoading(true);
    setAiLoadingMsg(`Translating to ${LANGUAGE_NAMES[targetLang]}...`);
    setErrorMsg(null);

    try {
      // If no current story, save first to create one
      let sourceStory = currentStory;
      let canonicalStory = currentStory;
      const sourceLanguage = currentStory?.language || language;

      if (!sourceStory) {
        setAiLoadingMsg('Saving story first...');
        sourceStory = await createSimpleStory({
          headline,
          deck,
          body,
          language: sourceLanguage,
        });
        if (!sourceStory) {
          throw new Error('Failed to save story before translation');
        }
        setCurrentStory(sourceStory);
        canonicalStory = sourceStory; // New story is its own canonical
      } else {
        // Save current changes first if needed
        if (headline !== sourceStory.headline || deck !== sourceStory.deck || body !== sourceStory.body) {
          setAiLoadingMsg('Saving changes first...');
          await handleSave();
          // Reload story to get updated version
          const updated = await getSimpleStory(sourceStory.id);
          if (updated) {
            sourceStory = updated;
            setCurrentStory(updated);
          }
        }

        // If story is in a group, get the canonical story (non-translation source)
        if (sourceStory.groupId) {
          setAiLoadingMsg('Finding source story...');
          const group = await getStoryGroup(sourceStory.groupId);
          if (group) {
            canonicalStory = getCanonicalStory(group);
            // If we're viewing a translation, we should still save any edits to it
            // but translate from the canonical
          }
        }
      }

      setAiLoadingMsg(`Translating to ${LANGUAGE_NAMES[targetLang]}...`);

      if (!canonicalStory) {
        throw new Error('No source story available for translation');
      }

      // Always translate from the canonical story, not from currentStory
      // This ensures we never translate a translation
      // Check if source is TV content - use enhanced translator for TV
      const isTvContent = canonicalStory.platform === 'tv' && (canonicalStory.platformMetadata.slug || canonicalStory.platformMetadata.anchorIntro || canonicalStory.platformMetadata.script);

      let translatedContent: {
        headline?: string;
        deck?: string;
        body?: string;
        slug?: string;
        anchor_intro?: string;
        script?: string;
      };

      if (isTvContent) {
        // TV content: use enhanced translator with TV fields
        const translated = await runTranslatorAgentV2({
          headline: canonicalStory.headline,
          deck: canonicalStory.deck,
          body: canonicalStory.body,
          slug: canonicalStory.platformMetadata.slug,
          anchorIntro: canonicalStory.platformMetadata.anchorIntro,
          script: canonicalStory.platformMetadata.script,
        }, canonicalStory.language, targetLang);

        // Check if result is an error
        if (isAgentError(translated)) {
          throw new Error(`Translation failed: ${translated.message}${translated.details ? ` (${translated.details.slice(0, 100)})` : ''}`);
        }

        translatedContent = translated as typeof translatedContent;
        if (!translatedContent || (!translatedContent.body && !translatedContent.script)) {
          throw new Error('Translation failed - no content returned');
        }
      } else {
        // Web content: use standard translator
        const translated = await runTranslatorAgent(
          canonicalStory.headline,
          canonicalStory.deck,
          canonicalStory.body,
          canonicalStory.language,
          targetLang
        );

        // Check if result is an error
        if ('error' in translated && translated.error) {
          const error = translated as AgentError;
          throw new Error(`Translation failed: ${error.message}${error.details ? ` (${error.details.slice(0, 100)})` : ''}`);
        }

        translatedContent = translated as typeof translatedContent;
        if (!translatedContent || !translatedContent.body) {
          throw new Error('Translation failed - no content returned');
        }
      }

      setAiLoadingMsg('Saving translation...');

      // Create a new story for the translation
      // Use canonicalStory.id as source to maintain proper linking
      const translatedStory = await createTranslation(
        canonicalStory.id,
        targetLang,
        {
          headline: translatedContent.headline || canonicalStory.headline,
          deck: translatedContent.deck || canonicalStory.deck,
          body: translatedContent.body || canonicalStory.body,
          // TV fields
          slug: translatedContent.slug || canonicalStory.platformMetadata.slug,
          anchorIntro: translatedContent.anchor_intro || canonicalStory.platformMetadata.anchorIntro,
          script: translatedContent.script || canonicalStory.platformMetadata.script,
        }
      );

      if (translatedStory) {
        // Load the new translated story
        await loadStory(translatedStory.id);
        setSuccessMsg(`Translated to ${LANGUAGE_NAMES[targetLang]}! Review and edit as needed.`);
        // Reload story groups to show updated group
        loadStories();
      } else {
        throw new Error('Failed to save translation');
      }
    } catch (error) {
      console.error('Translation error:', error);
      setErrorMsg(`Translation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setTranslating(false);
      setAiLoading(false);
      setAiLoadingMsg('');
      setTimeout(() => setSuccessMsg(null), 5000);
    }
  };

  const handleBack = () => {
    // Release checkout lock before navigating away
    if (hasCheckoutLock.current && currentStory?.id) {
      releaseCheckout(currentStory.id);
      hasCheckoutLock.current = false;
    }
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    setIsCheckedOutByOther(false);
    setLockHolderName('');
    setView('list');
    setCurrentStory(null);
    localStorage.removeItem('tolonews_current_story_id');
    setShowAiPanel(false);
    setShowVersionHistory(false);
    setShowEditorReviewPanel(false);
    setShowDiff(false);
    setAiIssues([]);
    setEditorFeedback([]);
    setScratchpad('');
    setEditorialNotes('');
    setShowScratchpad(false);
    loadStories();
  };

  const handleRestoreRevision = useCallback((fields: {
    headline: string;
    deck: string;
    body: string;
    slug: string;
    anchorIntro: string;
    script: string;
  }) => {
    setHeadline(fields.headline);
    setDeck(fields.deck);
    setBody(fields.body);
    setSlug(fields.slug);
    setAnchorIntro(fields.anchorIntro);
    setScript(fields.script);
    setHasUnsavedChanges(true);
    setSuccessMsg('Version restored. Review and save when ready.');
    setTimeout(() => setSuccessMsg(null), 3000);
  }, []);

  const handleDismissIssue = async (id: string) => {
    const updatedIssues = aiIssues.map((i) =>
      i.id === id ? { ...i, status: 'dismissed' as const, dismissedAt: new Date().toISOString() } : i
    );
    setAiIssues(updatedIssues);
    if (currentStory) {
      await saveSimpleStory(currentStory.id, { editorIssues: updatedIssues });
      setCurrentStory({ ...currentStory, editorIssues: updatedIssues });
    }
  };

  const handleMarkIssueApplied = async (id: string) => {
    const updatedIssues = aiIssues.map((i) =>
      i.id === id ? { ...i, status: 'applied' as const, appliedAt: new Date().toISOString() } : i
    );
    setAiIssues(updatedIssues);
    if (currentStory) {
      await saveSimpleStory(currentStory.id, { editorIssues: updatedIssues });
      setCurrentStory({ ...currentStory, editorIssues: updatedIssues });
    }
  };

  const wordCount = body.split(/\s+/).filter(Boolean).length;

  // Determine current language for RTL
  const currentLanguage = currentStory?.language || language;
  const textDirection = isRTL(currentLanguage) ? 'rtl' : 'ltr';

  // Non-blocking toast notification for AI processing (replaces full-screen overlay)
  const aiToast = aiLoading ? (
    <div className="fixed bottom-4 right-4 z-50 animate-slide-in-up">
      <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 border border-zinc-200 dark:border-zinc-700 min-w-[280px]">
        <div className={`h-8 w-8 rounded-full ${translating ? 'bg-teal-50 dark:bg-teal-900/30' : 'bg-purple-50 dark:bg-purple-900/30'} flex items-center justify-center flex-shrink-0`}>
          <Loader2 className={`h-4 w-4 animate-spin ${translating ? 'text-teal-600 dark:text-teal-400' : 'text-purple-600 dark:text-purple-400'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-zinc-900 dark:text-white truncate">
            {translating ? 'Translating...' : 'AI Review in Progress'}
          </p>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
            {aiLoadingMsg || 'You can continue editing...'}
          </p>
        </div>
      </div>
    </div>
  ) : null;

  // ==========================================================================
  // RENDER: Story List View
  // ==========================================================================
  if (view === 'list') {
    return (
      <>
      <div className="h-full bg-zinc-50 dark:bg-zinc-950 animate-fade-in">
        {/* Header */}
        <div className="bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 px-6 py-4">
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            <div>
              <h1 className="text-xl font-bold text-zinc-900 dark:text-white">
                My Stories
              </h1>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                Active stories first, then completed
              </p>
            </div>
            <Button
              onClick={handleNewStory}
              className="flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Story
            </Button>
          </div>
        </div>

        {/* Story List */}
        <div className="max-w-4xl mx-auto p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : storyGridList.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-zinc-300 dark:text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-500 dark:text-zinc-400 mb-4">
                No stories yet. Start writing!
              </p>
              <Button
                onClick={handleNewStory}
              >
                Create Your First Story
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {storyGridList.map((gridData, index) => (
                <div
                  key={gridData.groupId}
                  className="animate-fade-in animate-slide-in-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <StoryGrid
                    gridData={gridData}
                    onCellClick={async (platform, language, story) => {
                      if (story) {
                        await loadStory(story.id);
                        setView('editor');
                      }
                    }}
                    onGenerateClick={(platform, language) => {
                      handleGenerateDerivative(platform, language, gridData.canonicalStory);
                    }}
                    generatingCells={generatingCells.filter(c => c.groupId === gridData.groupId)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {aiToast}
      {/* Error/Success messages for list view */}
      {errorMsg && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-lg w-full mx-auto p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-between animate-fade-in shadow-lg">
          <div className="flex items-center">
            <AlertTriangle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0" />
            <span className="text-sm text-red-700 dark:text-red-400">{errorMsg}</span>
          </div>
          <Button onClick={() => setErrorMsg(null)} variant="ghost" size="icon" className="h-auto w-auto p-0 text-red-500 hover:text-red-600 ml-2">×</Button>
        </div>
      )}
      {successMsg && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-lg w-full mx-auto p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center justify-between animate-fade-in shadow-lg">
          <div className="flex items-center">
            <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
            <span className="text-sm text-green-700 dark:text-green-400">{successMsg}</span>
          </div>
          <Button onClick={() => setSuccessMsg(null)} variant="ghost" size="icon" className="h-auto w-auto p-0 text-green-500 hover:text-green-600 ml-2">×</Button>
        </div>
      )}
      {/* Derivative Generation Modal for list view */}
      <Dialog open={showDerivativeModal} onOpenChange={setShowDerivativeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Sparkles className="h-6 w-6 text-purple-500 mr-3" />
              Generate {derivativeTarget?.platform} Version
            </DialogTitle>
            <DialogDescription>
              This will create a new {LANGUAGE_NAMES[derivativeTarget?.language || 'eng']} {derivativeTarget?.platform} version
              based on the approved canonical story.
            </DialogDescription>
          </DialogHeader>
          <div className="p-3 bg-zinc-100 dark:bg-zinc-800 rounded my-4">
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              Source: {derivativeTarget?.sourceStory?.platformMetadata?.slug || derivativeTarget?.sourceStory?.headline}
            </p>
          </div>
          <div className="flex space-x-3">
            <Button
              onClick={() => {
                setShowDerivativeModal(false);
                setDerivativeTarget(null);
              }}
              variant="secondary"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={performDerivativeGeneration}
              className="flex-1 bg-purple-600 hover:bg-purple-700"
              disabled={generatingDerivative}
            >
              {generatingDerivative ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                'Generate'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {showLanguageSelector && selectedGroup && (
        <LanguageSelector
          group={selectedGroup}
          onSelectLanguage={handleSelectLanguage}
          onCreateTranslation={handleCreateTranslationFromGroup}
          onClose={() => {
            setShowLanguageSelector(false);
            setSelectedGroup(null);
          }}
        />
      )}
      </>
    );
  }

  // ==========================================================================
  // RENDER: Editor View
  // ==========================================================================
  return (
    <div className="h-full flex flex-col bg-zinc-50 dark:bg-zinc-950 animate-fade-in">
      {/* Header */}
      <div className="bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              onClick={handleBack}
              variant="ghost"
              size="icon"
              className="hover:bg-zinc-100 dark:hover:bg-zinc-800"
            >
              <ArrowLeft className="h-5 w-5 text-zinc-600 dark:text-zinc-400" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-zinc-900 dark:text-white">
                {currentStory ? 'Edit Story' : 'New Story'}
              </h1>
              {currentStory && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-zinc-500 dark:text-zinc-400">{LANGUAGE_NAMES[currentStory.language]}</span>
                  <span className="text-zinc-300 dark:text-zinc-600">•</span>
                  {/* Status Workflow Indicator */}
                  <div className="flex items-center gap-1">
                    {/* Draft */}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      currentStory.status === 'draft'
                        ? 'bg-zinc-200 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-200'
                        : 'text-zinc-400 dark:text-zinc-500'
                    }`}>
                      Draft
                    </span>
                    <span className="text-zinc-300 dark:text-zinc-600">→</span>
                    {/* Reviewed */}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      currentStory.status === 'reviewed'
                        ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                        : currentStory.status === 'pending_review' || currentStory.status === 'approved'
                        ? 'text-zinc-400 dark:text-zinc-500'
                        : 'text-zinc-300 dark:text-zinc-600'
                    }`}>
                      Reviewed
                    </span>
                    <span className="text-zinc-300 dark:text-zinc-600">→</span>
                    {/* Pending Review */}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      currentStory.status === 'pending_review'
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                        : currentStory.status === 'approved'
                        ? 'text-zinc-400 dark:text-zinc-500'
                        : 'text-zinc-300 dark:text-zinc-600'
                    }`}>
                      Pending Review
                    </span>
                    <span className="text-zinc-300 dark:text-zinc-600">→</span>
                    {/* Approved */}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex items-center ${
                      currentStory.status === 'approved'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                        : 'text-zinc-300 dark:text-zinc-600'
                    }`}>
                      {currentStory.status === 'approved' && <Lock className="h-3 w-3 mr-1" />}
                      Approved
                    </span>
                  </div>
                  {currentStory.isCanonical && (
                    <span className="ml-2 px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-[10px] font-medium text-blue-700 dark:text-blue-400">Canonical</span>
                  )}
                  {currentStory.derivedFromStoryId && (
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-[10px] font-medium text-purple-700 dark:text-purple-400">Derivative</span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center">
            {/* ── Group 1: Story Settings & Tools ── */}
            <div className="flex items-center gap-2">
              {/* Language selector (only for new stories) */}
              {!currentStory && (
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as LanguageCode)}
                  className="px-3 py-2 text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
                >
                  {availableLanguages.includes('fa-AF') && (
                    <option value="fa-AF">{LANGUAGE_NAMES['fa-AF']}</option>
                  )}
                  {availableLanguages.includes('ps-AF') && (
                    <option value="ps-AF">{LANGUAGE_NAMES['ps-AF']}</option>
                  )}
                  {availableLanguages.includes('eng') && (
                    <option value="eng">{LANGUAGE_NAMES['eng']}</option>
                  )}
                </select>
              )}

              {/* TV Format selector (only for new stories) */}
              {!currentStory && (
                <select
                  value={tvFormat}
                  onChange={(e) => setTvFormat(e.target.value as TvFormat)}
                  className="px-3 py-2 text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
                  title="Select TV format"
                >
                  <option value="short">{TV_FORMAT_LABELS['short']}</option>
                  <option value="full">{TV_FORMAT_LABELS['full']}</option>
                </select>
              )}

              {/* TV format badge for existing TV stories */}
              {currentStory && currentStory.platform === 'tv' && currentStory.platformMetadata.tvFormat && (
                <span className="px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-md">
                  {TV_FORMAT_LABELS[currentStory.platformMetadata.tvFormat]}
                </span>
              )}

              {/* Language switcher (for existing stories with group) */}
              {currentStory && currentGroup && currentGroup.stories.length > 1 && (
                <Button
                  onClick={() => {
                    setSelectedGroup(currentGroup);
                    setShowLanguageSelector(true);
                  }}
                  variant="outline"
                  size="sm"
                  className="flex items-center"
                  title="Switch language version"
                >
                  <Languages className="h-4 w-4 mr-2" />
                  <span>{LANGUAGE_LABELS[currentStory.language]}</span>
                  <span className="ml-2 text-xs text-zinc-500 dark:text-zinc-400">
                    ({currentGroup.availableLanguages.length})
                  </span>
                </Button>
              )}

              {/* Version History */}
              {currentStory && (
                <Button
                  onClick={() => {
                    const next = !showVersionHistory;
                    setShowVersionHistory(next);
                    if (next) {
                      setShowAiPanel(false);
                      setShowEditorReviewPanel(false);
                      setShowDiff(false);
                    }
                  }}
                  variant={showVersionHistory ? 'default' : 'outline'}
                  size="sm"
                  className={
                    showVersionHistory
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : ''
                  }
                  title="Version history"
                >
                  <History className="h-4 w-4 mr-1.5" />
                  History
                </Button>
              )}

              {/* Archive */}
              {currentStory && !isLocked && (
                <Button
                  onClick={handleArchive}
                  variant="ghost"
                  size="icon"
                  className="text-zinc-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                  title="Archive story"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* ── Divider ── */}
            {currentStory && (
              <div className="h-6 border-l border-zinc-200 dark:border-zinc-700 mx-3" />
            )}

            {/* ── Group 2: Workflow Actions ── */}
            <div className="flex items-center gap-2">
              {/* Translate (canonical, unlocked, has content, panel/history closed) */}
              {!showAiPanel && !showVersionHistory && !isLocked && currentStory?.isCanonical !== false && (
                <Button
                  onClick={async () => {
                    setShowTranslateModal(true);
                    setLoadingTranslationLanguages(true);
                    try {
                      const languages = await getAvailableTranslationLanguages();
                      setAvailableTranslationLanguages(languages);
                    } catch (error) {
                      console.error('Error loading translation languages:', error);
                      const currentLang = currentStory?.language || language;
                      setAvailableTranslationLanguages(SUPPORTED_LANGUAGES.filter((lang) => lang !== currentLang));
                    } finally {
                      setLoadingTranslationLanguages(false);
                    }
                  }}
                  disabled={aiLoading || translating || !(useTvEditor ? (anchorIntro.trim() || script.trim()) : body.trim())}
                  className="flex items-center bg-teal-600 hover:bg-teal-700"
                  title={!(useTvEditor ? (anchorIntro.trim() || script.trim()) : body.trim()) ? 'Write some content first before translating' : 'Translate to another language'}
                >
                  <Languages className="h-4 w-4 mr-2" />
                  Translate
                </Button>
              )}

              {/* Contextual CTA — adapts to story state + user role */}
              {!showAiPanel && !showVersionHistory && !isCheckedOutByOther && (
                <>
                  {/* Draft canonical with no issues: AI Review (also shows greyed-out for new unsaved stories) */}
                  {!isLocked && currentStory?.isCanonical !== false && currentStory?.status !== 'reviewed' && !isPendingReview && aiIssues.length === 0 && (
                    <Button
                      onClick={handleAiReview}
                      disabled={!currentStory || aiLoading || (useTvEditor ? !(anchorIntro.trim() || script.trim()) : !body.trim())}
                      className="flex items-center bg-purple-600 hover:bg-purple-700"
                      title={
                        !currentStory
                          ? 'Save the story first, then run AI Review'
                          : useTvEditor
                            ? (!(anchorIntro.trim() || script.trim()) ? 'Write anchor intro or script first' : 'Run AI review to polish your TV script')
                            : (!body.trim() ? 'Write some content first' : 'Run AI review to polish and check your story')
                      }
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      AI Review
                    </Button>
                  )}
                  {/* Has issues, panel closed: show issue count (pending only) */}
                  {(() => {
                    const pendingCount = aiIssues.filter(i => !i.status || i.status === 'pending').length;
                    return currentStory && !isLocked && pendingCount > 0 && currentStory.status !== 'reviewed' && !isPendingReview ? (
                      <Button
                        onClick={() => { setShowAiPanel(true); setShowVersionHistory(false); setShowEditorReviewPanel(false); }}
                        variant="outline"
                        className="flex items-center text-amber-600 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                        title="View pending issues from AI review"
                      >
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        {pendingCount} Issue{pendingCount !== 1 ? 's' : ''}
                      </Button>
                    ) : null;
                  })()}
                  {/* Reviewed: Everyone submits to editor's desk — no self-approval */}
                  {currentStory && currentStory.status === 'reviewed' && (
                    <Button
                      onClick={handleSubmitForReview}
                      disabled={saving || submittingForReview}
                      className="flex items-center bg-blue-600 hover:bg-blue-700"
                      title="Submit story for editor review and approval"
                    >
                      {submittingForReview ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Submit to Editor
                    </Button>
                  )}
                  {/* Pending Review: Author sees badge, different editor sees Review toggle */}
                  {currentStory && isPendingReview && (
                    isEditor && isNotAuthor ? (
                      <Button
                        onClick={() => {
                          const next = !showEditorReviewPanel;
                          setShowEditorReviewPanel(next);
                          if (next) { setShowAiPanel(false); setShowVersionHistory(false); }
                        }}
                        variant={showEditorReviewPanel ? 'default' : 'outline'}
                        className={showEditorReviewPanel ? 'bg-blue-600 text-white hover:bg-blue-700' : 'text-blue-600 border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20'}
                        title="Open editor review panel"
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Review
                      </Button>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                        <Clock className="h-4 w-4 mr-1.5" />
                        Awaiting Editor Review
                      </span>
                    )
                  )}
                  {/* Approved: Unlock */}
                  {isApprovalLocked && (
                    <Button
                      onClick={handleUnlock}
                      variant="outline"
                      disabled={unlocking}
                      className="text-amber-600 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                      title="Unlock story for editing"
                    >
                      {unlocking ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Unlock className="h-4 w-4 mr-2" />
                      )}
                      Unlock
                    </Button>
                  )}
                </>
              )}
            </div>

            {/* ── Divider ── */}
            {currentStory && (
              <div className="h-6 border-l border-zinc-200 dark:border-zinc-700 mx-3" />
            )}

            {/* ── Group 3: Save ── */}
            <div className="flex items-center gap-2">
              {lastSaved && !hasUnsavedChanges && (
                <span className="text-xs text-green-600 dark:text-green-400 flex items-center">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Saved {formatRelativeTime(lastSaved)}
                </span>
              )}
              {hasUnsavedChanges && currentStory && (
                <span className="text-xs text-amber-600 dark:text-amber-400">
                  Unsaved changes
                </span>
              )}
              <Button
                onClick={handleSave}
                disabled={saving || (useTvEditor ? !slug.trim() : !headline.trim())}
                className="flex items-center"
                title={useTvEditor
                  ? (!slug.trim() ? 'Add a slug to save' : 'Save your story')
                  : (!headline.trim() ? 'Add a headline to save' : 'Save your story')}
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {errorMsg && (
        <div className="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-between animate-fade-in animate-slide-in-down">
          <div className="flex items-center">
            <AlertTriangle className="h-4 w-4 text-red-500 mr-2" />
            <span className="text-sm text-red-700 dark:text-red-400">{errorMsg}</span>
          </div>
          <Button onClick={() => setErrorMsg(null)} variant="ghost" size="icon" className="h-auto w-auto p-0 text-red-500 hover:text-red-600">×</Button>
        </div>
      )}
      {successMsg && (
        <div className="mx-6 mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center justify-between animate-fade-in animate-slide-in-down">
          <div className="flex items-center">
            <Check className="h-4 w-4 text-green-500 mr-2" />
            <span className="text-sm text-green-700 dark:text-green-400">{successMsg}</span>
          </div>
          <Button onClick={() => setSuccessMsg(null)} variant="ghost" size="icon" className="h-auto w-auto p-0 text-green-500 hover:text-green-600">×</Button>
        </div>
      )}

      {/* Version Conflict Modal — blocking overlay, not dismissable except by refresh */}
      {versionConflict && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-zinc-900/70 backdrop-blur-sm">
          <div className="max-w-md w-full mx-4 bg-white dark:bg-zinc-800 rounded-xl shadow-xl border-2 border-red-400 dark:border-red-600 overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="bg-red-100 dark:bg-red-900/40 p-3 rounded-full flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-zinc-900 dark:text-white">Version Conflict</h3>
                  <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">
                    This story was updated in another tab or device. Please copy any unsaved text and refresh to see the latest changes.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-zinc-50 dark:bg-zinc-900/50 px-6 py-4 flex justify-end gap-3 border-t border-zinc-200 dark:border-zinc-700">
              <Button
                onClick={() => window.location.reload()}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Page
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Language Mismatch Warning Modal */}
      {languageWarning && (
        <Dialog open={!!languageWarning} onOpenChange={() => setLanguageWarning(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <AlertTriangle className="h-6 w-6 text-amber-500 mr-3" />
                Language Mismatch
              </DialogTitle>
              <DialogDescription>
                {languageWarning.message}
              </DialogDescription>
            </DialogHeader>
            <div className="flex space-x-3 mt-4">
              <Button
                onClick={() => setLanguageWarning(null)}
                variant="secondary"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const newLang = languageWarning!.suggestedLang;
                  setLanguage(newLang);
                  setLanguageWarning(null);
                  setHasUnsavedChanges(true);
                  setSuccessMsg(`Switched to ${LANGUAGE_NAMES[newLang]} — save to apply`);
                  setTimeout(() => setSuccessMsg(null), 4000);
                }}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
              >
                Switch to {LANGUAGE_NAMES[languageWarning.suggestedLang]}
              </Button>
              {langWarningSource === 'aiReview' ? (
                <Button
                  onClick={() => {
                    setLanguageWarning(null);
                    executeAiReview();
                  }}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  Review Anyway
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    setLanguageWarning(null);
                    setHasUnsavedChanges(true);
                  }}
                  className="flex-1 bg-zinc-600 hover:bg-zinc-700"
                >
                  Keep {LANGUAGE_NAMES[language]}
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Translation Modal */}
      <Dialog open={showTranslateModal} onOpenChange={setShowTranslateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Languages className="h-6 w-6 text-teal-500 mr-3" />
              Translate Story
            </DialogTitle>
            <DialogDescription>
              Current language: <span className="font-medium">{LANGUAGE_NAMES[currentStory?.language || language]}</span>
              {currentStory?.groupId 
                ? ' This will create a new translation from the original story. The original will be preserved.'
                : ' This will create a new linked translation. The original story will be preserved.'}
            </DialogDescription>
          </DialogHeader>
          {loadingTranslationLanguages ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-teal-500" />
                <span className="ml-2 text-sm text-zinc-500 dark:text-zinc-400">Loading available languages...</span>
              </div>
            ) : availableTranslationLanguages.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-2">
                  All languages have been translated.
                </p>
                <p className="text-xs text-zinc-400 dark:text-zinc-500">
                  You can still edit existing translations manually.
                </p>
              </div>
          ) : (
            <div className="space-y-2">
              {availableTranslationLanguages.map((targetLang) => (
                <Button
                  key={targetLang}
                  onClick={() => handleTranslate(targetLang)}
                  variant="outline"
                  className="w-full flex items-center justify-between px-4 py-3 bg-zinc-50 dark:bg-zinc-800 hover:bg-teal-50 dark:hover:bg-teal-900/20 border-zinc-200 dark:border-zinc-700 hover:border-teal-300 dark:hover:border-teal-700 group"
                >
                  <div className="flex items-center">
                    <span className="text-lg mr-3" dir={isRTL(targetLang) ? 'rtl' : 'ltr'}>
                      {LANGUAGE_LABELS[targetLang]}
                    </span>
                    <span className="text-sm text-zinc-500 dark:text-zinc-400">
                      {LANGUAGE_NAMES[targetLang]}
                    </span>
                  </div>
                  <span className="text-teal-600 dark:text-teal-400 opacity-0 group-hover:opacity-100 transition-opacity">
                    Translate →
                  </span>
                </Button>
              ))}
            </div>
          )}
          <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-4 text-center">
            AI translation will preserve facts while adapting to journalistic standards
          </p>
        </DialogContent>
      </Dialog>

      {/* Unlock Warning Dialog (Phase 3) */}
      <Dialog open={showUnlockWarning} onOpenChange={setShowUnlockWarning}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <AlertTriangle className="h-6 w-6 text-amber-500 mr-3" />
              Unlock Canonical Story?
            </DialogTitle>
            <DialogDescription>
              This is a canonical story with derivatives (web articles, translations, social posts).
              Editing the canonical may make derivatives out of sync.
            </DialogDescription>
          </DialogHeader>
          <div className="flex space-x-3 mt-4">
            <Button
              onClick={() => setShowUnlockWarning(false)}
              variant="secondary"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={performUnlock}
              className="flex-1 bg-amber-600 hover:bg-amber-700"
              disabled={unlocking}
            >
              {unlocking ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Unlocking...
                </>
              ) : (
                'Unlock Anyway'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Editor Reject / Send Back Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <RotateCcw className="h-6 w-6 text-red-500 mr-3" />
              Send Story Back
            </DialogTitle>
            <DialogDescription>
              Provide feedback for the journalist explaining what needs to be fixed.
              The story will return to draft status.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <textarea
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              rows={4}
              dir={textDirection}
              placeholder="What needs to be fixed or improved..."
              className="w-full text-sm text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700 rounded-lg p-3 focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none bg-white dark:bg-zinc-800 placeholder-zinc-400 dark:placeholder-zinc-600"
              autoFocus
            />
            <div className="flex space-x-3">
              <Button
                onClick={() => { setShowRejectDialog(false); setRejectNotes(''); }}
                variant="secondary"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleEditorReject()}
                disabled={!rejectNotes.trim()}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Send Back
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Derivative Generation Modal (Phase 3) */}
      <Dialog open={showDerivativeModal} onOpenChange={setShowDerivativeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Sparkles className="h-6 w-6 text-purple-500 mr-3" />
              Generate {derivativeTarget?.platform} Version
            </DialogTitle>
            <DialogDescription>
              This will create a new {LANGUAGE_NAMES[derivativeTarget?.language || 'eng']} {derivativeTarget?.platform} version
              based on the approved canonical story.
            </DialogDescription>
          </DialogHeader>
          <div className="p-3 bg-zinc-100 dark:bg-zinc-800 rounded my-4">
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              Source: {derivativeTarget?.sourceStory?.platformMetadata?.slug || derivativeTarget?.sourceStory?.headline || currentStory?.platformMetadata?.slug || currentStory?.headline}
            </p>
          </div>
          <div className="flex space-x-3">
            <Button
              onClick={() => {
                setShowDerivativeModal(false);
                setDerivativeTarget(null);
              }}
              variant="secondary"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={performDerivativeGeneration}
              className="flex-1 bg-purple-600 hover:bg-purple-700"
              disabled={generatingDerivative}
            >
              {generatingDerivative ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                'Generate'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Editor */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Diff toolbar */}
          {showAiPanel && (
            <div className="bg-white dark:bg-zinc-900 px-6 py-2 border-b border-zinc-100 dark:border-zinc-800 flex justify-between items-center">
              <span className="text-sm text-zinc-600 dark:text-zinc-400">
                {showDiff ? 'Comparing changes' : 'AI-polished draft'}
              </span>
              <div className="flex items-center space-x-2">
                {showDiff && (useTvEditor ? (originalSlug || originalAnchorIntro || originalScript) : originalBody) && (
                  <span className="text-xs text-zinc-500 bg-zinc-100 dark:bg-zinc-800 px-2 py-1 rounded">
                    {(() => {
                      // For TV, combine all fields; for web, just use body
                      const oldText = useTvEditor
                        ? `${originalSlug}\n${originalAnchorIntro}\n${originalScript}`
                        : originalBody;
                      const newText = useTvEditor
                        ? `${slug}\n${anchorIntro}\n${script}`
                        : body;
                      const diffs = diffWords(oldText, newText);
                      const stats = getDiffStats(diffs);
                      return (
                        <>
                          <span className="text-green-600">+{stats.added}</span> /{' '}
                          <span className="text-red-500">-{stats.removed}</span>
                        </>
                      );
                    })()}
                  </span>
                )}
                <Button
                  onClick={() => setShowDiff(!showDiff)}
                  variant={showDiff ? "default" : "secondary"}
                  size="sm"
                  className="text-xs"
                >
                  <Split className="h-3 w-3 inline mr-1" />
                  {showDiff ? 'Hide Changes' : 'Show Changes'}
                </Button>
              </div>
            </div>
          )}

          {/* Warning: Invalid platform-language combination */}
          {isInvalidPlatformLanguage && (
            <div className="mx-6 mt-4 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 p-3 rounded-lg flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">
                {language === 'eng' && currentStory?.platform === 'tv'
                  ? 'English is not available for TV broadcast. TOLOnews broadcasts in Dari and Pashto only.'
                  : 'This platform-language combination is not configured. Please select a different language.'}
              </span>
            </div>
          )}

          {/* Badge: Light review mode for derivatives */}
          {reviewMode === 'light' && currentStory && !currentStory.isCanonical && (
            <div className="mx-6 mt-2 flex items-center gap-2">
              <span className="px-2 py-0.5 text-xs bg-zinc-200 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 rounded">
                Derivative Content
              </span>
              <span className="text-xs text-zinc-500 dark:text-zinc-400">
                AI Review is disabled for derivatives. Review runs on canonical source only.
              </span>
            </div>
          )}

          {/* Editor fields - TV Script or Web Article based on platform */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-3xl mx-auto space-y-8">
              {/* Heartbeat Warning — consecutive heartbeat failures mean editing lock may expire */}
              {heartbeatWarning && hasCheckoutLock.current && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg p-4 flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-yellow-800 dark:text-yellow-200">
                      Connection issue — your editing lock may expire
                    </p>
                    <p className="text-sm text-yellow-600 dark:text-yellow-400">
                      Save your work now. The lock will expire after 3 minutes of failed heartbeats.
                    </p>
                  </div>
                </div>
              )}

              {/* Checkout Lock Banner — another user is editing */}
              {isCheckedOutByOther && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
                  <Lock className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-red-800 dark:text-red-200">
                      This story is being edited by {lockHolderName}
                    </p>
                    <p className="text-sm text-red-600 dark:text-red-400">
                      You are in read-only mode. The lock expires automatically after 3 minutes of inactivity.
                    </p>
                  </div>
                  <button
                    onClick={() => currentStory && loadStory(currentStory.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-900/60 rounded-lg transition-colors flex-shrink-0"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Check Again
                  </button>
                </div>
              )}

              {/* Rejection Notes Banner — shown when editor sent story back */}
              {currentStory?.status === 'draft' && currentStory?.rejectionNotes && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3" dir={textDirection}>
                  <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-red-800 dark:text-red-200">
                      Story sent back by editor
                    </p>
                    <p className="text-sm text-red-700 dark:text-red-300 mt-1 whitespace-pre-wrap">
                      {currentStory.rejectionNotes}
                    </p>
                  </div>
                </div>
              )}

              {/* Editor Feedback Cards — shown below rejection banner when story was sent back */}
              {currentStory?.status === 'draft' && currentStory?.editorFeedback && currentStory.editorFeedback.length > 0 && (
                <div className="space-y-2" dir={textDirection}>
                  {currentStory.editorFeedback.map((card: EditorFeedbackCard) => (
                    <div key={card.id} className={`border rounded-lg p-3 flex items-start gap-3 ${
                      card.severity === 'must_fix'
                        ? 'bg-red-50 dark:bg-red-900/15 border-red-200 dark:border-red-800/40'
                        : card.severity === 'note'
                        ? 'bg-zinc-50 dark:bg-zinc-800/50 border-zinc-200 dark:border-zinc-700'
                        : 'bg-blue-50 dark:bg-blue-900/15 border-blue-200 dark:border-blue-800/40'
                    }`}>
                      <MessageSquare className={`h-4 w-4 flex-shrink-0 mt-0.5 ${
                        card.severity === 'must_fix' ? 'text-red-500' : card.severity === 'note' ? 'text-zinc-400' : 'text-blue-500'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[10px] uppercase text-zinc-500 dark:text-zinc-400">{card.location.replace('_', ' ')}</span>
                          {card.severity && (
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                              card.severity === 'must_fix' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                              card.severity === 'note' ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400' :
                              'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                            }`}>
                              {card.severity === 'must_fix' ? 'Must Fix' : card.severity === 'note' ? 'Note' : 'Suggestion'}
                            </span>
                          )}
                          {card.createdByName && (
                            <span className="text-[10px] text-zinc-400 dark:text-zinc-500">{card.createdByName}</span>
                          )}
                        </div>
                        {card.quotedText && (
                          <p className="text-xs text-amber-700 dark:text-amber-400 italic bg-amber-50 dark:bg-amber-900/15 rounded px-2 py-1 mb-1 line-clamp-2" dir={textDirection}>
                            &ldquo;{card.quotedText}&rdquo;
                          </p>
                        )}
                        <p className="text-sm text-zinc-700 dark:text-zinc-300">{card.text}</p>
                        {card.suggestion && (
                          <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                            &rarr; {card.suggestion}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pending Review Banner — shown to journalists when story is pending */}
              {isPendingReview && !isEditor && !isCheckedOutByOther && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-center gap-3">
                  <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-blue-800 dark:text-blue-200">
                      Submitted for editor review
                    </p>
                    <p className="text-sm text-blue-600 dark:text-blue-400">
                      You cannot edit until the editor approves or sends it back.
                    </p>
                  </div>
                </div>
              )}

              {/* Locked Banner for approved stories */}
              {isApprovalLocked && !isCheckedOutByOther && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-center gap-3">
                  <Lock className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-amber-800 dark:text-amber-200">
                      This story is approved and locked
                    </p>
                    <p className="text-sm text-amber-600 dark:text-amber-400">
                      Click "Unlock" in the header to make changes. {currentStory?.isCanonical && 'Warning: Editing may affect derivatives.'}
                    </p>
                  </div>
                </div>
              )}

              {useTvEditor ? (
                /* ===== TV SCRIPT EDITOR ===== */
                <>
                  {/* Slug (story identifier) - with diff support */}
                  {showDiff && originalSlug ? (
                    <div className="text-2xl font-bold text-zinc-900 dark:text-white break-words overflow-x-hidden whitespace-pre-wrap" dir={textDirection}>
                      {diffWords(originalSlug, slug).map((part, i) => {
                        if (part.added)
                          return (
                            <span key={i} className="bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 px-0.5 rounded" style={{ unicodeBidi: 'plaintext' }}>
                              {part.value}
                            </span>
                          );
                        if (part.removed)
                          return (
                            <span key={i} className="bg-red-50 dark:bg-red-900/30 text-red-400 line-through opacity-60" style={{ unicodeBidi: 'plaintext' }}>
                              {part.value}
                            </span>
                          );
                        return <span key={i} style={{ unicodeBidi: 'plaintext' }}>{part.value}</span>;
                      })}
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      disabled={isLocked}
                      placeholder="Story Slug (identifier)"
                      dir={textDirection}
                      className="w-full text-2xl font-bold border-none p-0 focus:ring-0 text-zinc-900 dark:text-white placeholder-zinc-300 dark:placeholder-zinc-700 bg-transparent"
                    />
                  )}

                  {/* Anchor Intro - with diff support */}
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-2">
                      Anchor Introduction (~15 seconds)
                    </label>
                    {showDiff && originalAnchorIntro ? (
                      <div className="w-full text-lg text-zinc-600 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-700 rounded-lg p-3 bg-white dark:bg-zinc-800 whitespace-pre-wrap break-words overflow-x-hidden" dir={textDirection}>
                        {diffWords(originalAnchorIntro, anchorIntro).map((part, i) => {
                          if (part.added)
                            return (
                              <span key={i} className="bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 px-0.5 rounded" style={{ unicodeBidi: 'plaintext' }}>
                                {part.value}
                              </span>
                            );
                          if (part.removed)
                            return (
                              <span key={i} className="bg-red-50 dark:bg-red-900/30 text-red-400 line-through opacity-60" style={{ unicodeBidi: 'plaintext' }}>
                                {part.value}
                              </span>
                            );
                          return <span key={i} style={{ unicodeBidi: 'plaintext' }}>{part.value}</span>;
                        })}
                      </div>
                    ) : (
                      <textarea
                        value={anchorIntro}
                        onChange={(e) => setAnchorIntro(e.target.value)}
                        onSelect={() => handleEditorSelect('anchor_intro')}
                        disabled={isLocked}
                        rows={3}
                        placeholder="2-4 sentences for the anchor to read on camera..."
                        dir={textDirection}
                        className="w-full text-lg text-zinc-600 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-700 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none bg-white dark:bg-zinc-800"
                      />
                    )}
                  </div>

                  <div className="w-full h-px bg-zinc-200 dark:bg-zinc-800" />

                  {/* Script (main VO content) - with diff support */}
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-2">
                      Voice-Over Script
                    </label>
                    {showDiff && originalScript ? (
                      <div className="w-full min-h-[350px] text-lg leading-relaxed text-zinc-800 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 rounded-lg p-3 bg-white dark:bg-zinc-800 whitespace-pre-wrap break-words overflow-x-hidden" dir={textDirection}>
                        {diffWords(originalScript, script).map((part, i) => {
                          if (part.added)
                            return (
                              <span key={i} className="bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 px-0.5 rounded" style={{ unicodeBidi: 'plaintext' }}>
                                {part.value}
                              </span>
                            );
                          if (part.removed)
                            return (
                              <span key={i} className="bg-red-50 dark:bg-red-900/30 text-red-400 line-through opacity-60" style={{ unicodeBidi: 'plaintext' }}>
                                {part.value}
                              </span>
                            );
                          return <span key={i} style={{ unicodeBidi: 'plaintext' }}>{part.value}</span>;
                        })}
                      </div>
                    ) : (
                      <textarea
                        value={script}
                        onChange={(e) => setScript(e.target.value)}
                        onSelect={() => handleEditorSelect('script')}
                        disabled={isLocked}
                        placeholder="Main voice-over script content..."
                        dir={textDirection}
                        className="w-full min-h-[350px] text-lg leading-relaxed text-zinc-800 dark:text-zinc-200 placeholder-zinc-300 dark:placeholder-zinc-700 border border-zinc-200 dark:border-zinc-700 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none bg-white dark:bg-zinc-800"
                      />
                    )}
                  </div>

                  {/* Duration Breakdown */}
                  <div className="pt-4 border-t border-zinc-100 dark:border-zinc-800 space-y-3">
                    {durationBreakdown && effectiveWordTargets ? (
                      <>
                        {/* Anchor Intro Row */}
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-zinc-500 dark:text-zinc-400 w-24">Anchor Intro</span>
                          <div className="flex items-center gap-4">
                            <span className={`tabular-nums font-medium ${getDurationStatusColor(durationBreakdown.anchorDuration, durationBreakdown.anchorTarget)}`}>
                              ⏱ {formatDuration(durationBreakdown.anchorDuration)} / {formatDuration(durationBreakdown.anchorTarget)}
                              <span className="ml-1">{getDurationStatusIcon(durationBreakdown.anchorDuration, durationBreakdown.anchorTarget)}</span>
                            </span>
                            <span className="tabular-nums text-zinc-400 dark:text-zinc-500 text-xs">
                              ({durationBreakdown.anchorWords} words)
                            </span>
                          </div>
                        </div>

                        {/* Script Row */}
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-zinc-500 dark:text-zinc-400 w-24">Script</span>
                          <div className="flex items-center gap-4">
                            <span className={`tabular-nums font-medium ${getDurationStatusColor(durationBreakdown.scriptDuration, durationBreakdown.scriptTarget)}`}>
                              ⏱ {formatDuration(durationBreakdown.scriptDuration)} / {formatDuration(durationBreakdown.scriptTarget)}
                              <span className="ml-1">{getDurationStatusIcon(durationBreakdown.scriptDuration, durationBreakdown.scriptTarget)}</span>
                            </span>
                            <span className="tabular-nums text-zinc-400 dark:text-zinc-500 text-xs">
                              ({durationBreakdown.scriptWords} words)
                            </span>
                          </div>
                        </div>

                        {/* Divider */}
                        <div className="border-t border-zinc-200 dark:border-zinc-700"></div>

                        {/* Total Row */}
                        <div className="flex items-center justify-between text-sm font-medium">
                          <span className="text-zinc-700 dark:text-zinc-200 w-24">Total</span>
                          <div className="flex items-center gap-4">
                            <span className="tabular-nums text-zinc-700 dark:text-zinc-200">
                              {durationBreakdown.totalWords} words
                            </span>
                            <span className={`tabular-nums ${getDurationStatusColor(durationBreakdown.totalDuration, durationTarget)}`}>
                              ⏱ {formatDuration(durationBreakdown.totalDuration)} / {formatDuration(durationTarget)}
                              {durationBreakdown.totalDuration !== durationTarget && (
                                <span className="ml-1 text-xs">
                                  ({durationBreakdown.totalDuration > durationTarget ? '+' : ''}{durationBreakdown.totalDuration - durationTarget}s)
                                </span>
                              )}
                            </span>
                          </div>
                        </div>

                        {/* Duration Target Editor */}
                        <div className="flex items-center justify-between text-xs pt-2">
                          <div className="text-zinc-400 dark:text-zinc-500">
                            Target: {formatDuration(effectiveDurationTargets?.anchorDuration || 0)} + {formatDuration(effectiveDurationTargets?.scriptDuration || 0)} = {formatDuration(durationTarget)}
                            {customDurationTargets && <span className="ml-1 text-blue-500">(custom)</span>}
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowDurationTargetEditor(!showDurationTargetEditor)}
                            className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            {showDurationTargetEditor ? 'Hide' : 'Edit Target'}
                          </button>
                        </div>

                        {/* Editable Duration Targets */}
                        {showDurationTargetEditor && effectiveDurationTargets && (
                          <div className="flex flex-wrap items-center gap-4 text-xs bg-zinc-50 dark:bg-zinc-800/50 p-3 rounded-lg">
                            <div className="flex items-center gap-2">
                              <label className="text-zinc-500 dark:text-zinc-400">Anchor:</label>
                              <input
                                type="number"
                                min="5"
                                max="60"
                                value={customDurationTargets?.anchor ?? effectiveDurationTargets.anchorDuration}
                                onChange={(e) => setCustomDurationTargets(prev => ({
                                  ...prev,
                                  anchor: parseInt(e.target.value) || undefined
                                }))}
                                className="w-14 px-2 py-1 text-sm border border-zinc-300 dark:border-zinc-600 rounded bg-white dark:bg-zinc-700 text-zinc-800 dark:text-zinc-200"
                              />
                              <span className="text-zinc-400">sec</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <label className="text-zinc-500 dark:text-zinc-400">Script:</label>
                              <input
                                type="number"
                                min="10"
                                max="300"
                                value={customDurationTargets?.script ?? effectiveDurationTargets.scriptDuration}
                                onChange={(e) => setCustomDurationTargets(prev => ({
                                  ...prev,
                                  script: parseInt(e.target.value) || undefined
                                }))}
                                className="w-14 px-2 py-1 text-sm border border-zinc-300 dark:border-zinc-600 rounded bg-white dark:bg-zinc-700 text-zinc-800 dark:text-zinc-200"
                              />
                              <span className="text-zinc-400">sec</span>
                            </div>
                            <div className="text-zinc-400 border-l border-zinc-300 dark:border-zinc-600 pl-4">
                              ≈ {effectiveWordTargets?.anchorWords || 0} + {effectiveWordTargets?.scriptWords || 0} words
                            </div>
                            {customDurationTargets && (
                              <button
                                type="button"
                                onClick={() => setCustomDurationTargets(null)}
                                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                              >
                                Reset
                              </button>
                            )}
                          </div>
                        )}

                        {/* Over-target warning */}
                        {durationBreakdown.scriptDuration > durationBreakdown.scriptTarget && (
                          <div className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                            <AlertTriangle size={12} />
                            Script is {durationBreakdown.scriptDuration - durationBreakdown.scriptTarget}s over target ({durationBreakdown.scriptWords - effectiveWordTargets.scriptWords} words)
                          </div>
                        )}
                      </>
                    ) : (
                      /* Fallback to simple display when pacing not loaded */
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-zinc-400 dark:text-zinc-500">
                          {countWords(anchorIntro) + countWords(script)} words
                        </div>
                        <div className={`flex items-center gap-2 text-sm font-medium ${getDurationStatusColor(durationEstimate, durationTarget)}`}>
                          <span className="text-lg">⏱</span>
                          <span>{formatDuration(durationEstimate)}</span>
                          <span className="text-zinc-400">/</span>
                          <span className="text-zinc-500 dark:text-zinc-400">{formatDuration(durationTarget)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : currentStory?.platform === 'social' ? (
                /* ===== SOCIAL STORY — no editor fields, social cards shown below ===== */
                <div className="text-center py-8 text-zinc-500 dark:text-zinc-400">
                  <Share2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Social media derivative — see posts below</p>
                </div>
              ) : (
                /* ===== WEB ARTICLE EDITOR ===== */
                <>
                  {/* Headline */}
                  <input
                    type="text"
                    value={headline}
                    onChange={(e) => setHeadline(e.target.value)}
                    onSelect={() => handleEditorSelect('headline')}
                    disabled={showDiff || isLocked}
                    placeholder="Headline"
                    dir={textDirection}
                    className="w-full text-2xl font-bold border-none p-0 focus:ring-0 text-zinc-900 dark:text-white placeholder-zinc-300 dark:placeholder-zinc-700 bg-transparent"
                  />

                  {/* Deck/Summary */}
                  <textarea
                    value={deck}
                    onChange={(e) => setDeck(e.target.value)}
                    onSelect={() => handleEditorSelect('deck')}
                    disabled={showDiff || isLocked}
                    rows={2}
                    placeholder="Summary / Deck..."
                    dir={textDirection}
                    className="w-full text-lg text-zinc-600 dark:text-zinc-400 border-none p-0 focus:ring-0 resize-none italic bg-transparent"
                  />

                  <div className="w-full h-px bg-zinc-200 dark:bg-zinc-800" />

                  {/* Body - diff view or editable */}
                  {showDiff && originalBody ? (
                    <div className="text-lg leading-relaxed whitespace-pre-wrap break-words overflow-x-hidden" dir={textDirection}>
                      {diffWords(originalBody, body).map((part, i) => {
                        if (part.added)
                          return (
                            <span key={i} className="bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 px-0.5 rounded" style={{ unicodeBidi: 'plaintext' }}>
                              {part.value}
                            </span>
                          );
                        if (part.removed)
                          return (
                            <span key={i} className="bg-red-50 dark:bg-red-900/30 text-red-400 line-through opacity-60" style={{ unicodeBidi: 'plaintext' }}>
                              {part.value}
                            </span>
                          );
                        return <span key={i} className="text-zinc-800 dark:text-zinc-200" style={{ unicodeBidi: 'plaintext' }}>{part.value}</span>;
                      })}
                    </div>
                  ) : (
                    <textarea
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      onSelect={() => handleEditorSelect('body')}
                      disabled={isLocked}
                      placeholder="Start writing your story..."
                      dir={textDirection}
                      className={`w-full min-h-[400px] text-lg leading-relaxed text-zinc-800 dark:text-zinc-200 placeholder-zinc-300 dark:placeholder-zinc-700 border-none p-0 focus:ring-0 resize-none bg-transparent ${isLocked ? 'cursor-not-allowed opacity-75' : ''}`}
                    />
                  )}

                  {/* Word count */}
                  <div className="text-xs text-zinc-400 dark:text-zinc-500 pt-4 border-t border-zinc-100 dark:border-zinc-800">
                    {wordCount} words
                  </div>
                </>
              )}

              {/* Scratchpad & Editorial Notes — collapsible section */}
              {currentStory && (
                <div className="mt-8 pt-6 border-t border-zinc-200 dark:border-zinc-800">
                  <button
                    onClick={() => setShowScratchpad(!showScratchpad)}
                    className="flex items-center gap-2 text-sm font-medium text-zinc-600 dark:text-zinc-400 hover:text-zinc-800 dark:hover:text-zinc-200 transition-colors mb-3"
                  >
                    <ChevronDown className={`h-4 w-4 transition-transform ${showScratchpad ? '' : '-rotate-90'}`} />
                    <StickyNote className="h-4 w-4" />
                    Notes & Scratchpad
                    {(scratchpad || editorialNotes) && !showScratchpad && (
                      <span className="ml-1 w-2 h-2 rounded-full bg-blue-500 inline-block" />
                    )}
                  </button>

                  {showScratchpad && (
                    <div className="space-y-4">
                      {/* Editorial Notes — shared instructions from editors */}
                      <div>
                        <label className="flex items-center gap-1.5 text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1.5">
                          <MessageSquare className="h-3.5 w-3.5" />
                          Editorial Notes
                        </label>
                        <textarea
                          value={editorialNotes}
                          onChange={(e) => setEditorialNotes(e.target.value)}
                          disabled={isLocked}
                          rows={3}
                          placeholder="Instructions from editors, style guidance, angle notes..."
                          className="w-full text-sm text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none bg-white dark:bg-zinc-800 placeholder-zinc-400 dark:placeholder-zinc-600"
                        />
                      </div>

                      {/* Scratchpad — writer's raw workspace */}
                      <div>
                        <label className="flex items-center gap-1.5 text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1.5">
                          <StickyNote className="h-3.5 w-3.5" />
                          Scratchpad
                        </label>
                        <textarea
                          value={scratchpad}
                          onChange={(e) => setScratchpad(e.target.value)}
                          disabled={isLocked}
                          rows={4}
                          placeholder="Raw notes, phone numbers, quotes to verify, rough drafts..."
                          className="w-full text-sm text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none bg-zinc-50 dark:bg-zinc-800/50 placeholder-zinc-400 dark:placeholder-zinc-600 font-mono"
                        />
                      </div>

                      <p className="text-xs text-zinc-400 dark:text-zinc-500">
                        Both fields are saved with the story and visible to all editors.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Facts Panel - Show for TV stories with a group */}
              {currentStory && currentStory.groupId && currentStory.platform === 'tv' && (
                <div className="mt-8 pt-6 border-t border-zinc-200 dark:border-zinc-800">
                  <FactsPanel
                    key={factsRefreshKey}
                    groupId={currentStory.groupId}
                    language={currentStory.language}
                  />
                </div>
              )}

              {/* Social Media Posts Section - Show for approved stories OR social-platform derivatives */}
              {currentStory && (currentStory.status === 'approved' || (currentStory.platform === 'social' && (currentStory.socialPosts || currentStory.body))) && (() => {
                // Backwards compat: resolve socialPosts from body JSON for existing social derivatives
                const effectiveSocialPosts = currentStory.socialPosts || (() => {
                  if (currentStory.platform === 'social' && currentStory.body && !currentStory.socialPosts) {
                    try {
                      const parsed = JSON.parse(currentStory.body);
                      if (parsed && Array.isArray(parsed.posts)) return parsed;
                    } catch { /* not JSON, ignore */ }
                  }
                  return null;
                })();
                const hasSocialPosts = !!effectiveSocialPosts;
                return (
                <div className="mt-10 pt-8 border-t border-zinc-200 dark:border-zinc-800">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${hasSocialPosts ? 'bg-green-50 dark:bg-green-900/20' : 'bg-blue-50 dark:bg-blue-900/20'}`}>
                        {hasSocialPosts ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                        ) : (
                          <Share2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">
                          Social Media Posts
                        </h3>
                        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                          {hasSocialPosts ? (
                            <span className="text-green-600 dark:text-green-400">
                              Generated • Copy and post to your accounts
                            </span>
                          ) : (
                            'Not generated yet'
                          )}
                        </p>
                      </div>
                    </div>
                    {currentStory.platform !== 'social' && (
                    <Button
                      onClick={handleGenerateSocialPosts}
                      disabled={generatingSocialPosts || (useTvEditor ? (!anchorIntro.trim() && !script.trim()) : (!headline.trim() || !body.trim()))}
                      size="sm"
                      className="flex items-center"
                      title={hasSocialPosts ? 'Regenerate social media posts' : 'Generate social media posts'}
                    >
                      {generatingSocialPosts ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Generating...
                        </>
                      ) : (
                        <>
                          {hasSocialPosts ? (
                            <>
                              <RotateCcw className="h-4 w-4 mr-2" />
                              Regenerate
                            </>
                          ) : (
                            <>
                              <Share2 className="h-4 w-4 mr-2" />
                              Generate Posts
                            </>
                          )}
                        </>
                      )}
                    </Button>
                    )}
                  </div>

                  {generatingSocialPosts && !hasSocialPosts && (
                    <div className="flex items-center justify-center py-8 text-zinc-500 dark:text-zinc-400">
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      <span className="text-sm">Generating social media posts...</span>
                    </div>
                  )}

                  {socialPostsError && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-sm text-red-700 dark:text-red-400">{socialPostsError}</p>
                    </div>
                  )}

                  {effectiveSocialPosts && effectiveSocialPosts.posts && (
                    <div className="space-y-4">
                      {/* [LINK] placeholder info note */}
                      <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                        <LinkIcon className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-amber-700 dark:text-amber-300">
                          Replace <span className="font-mono font-semibold bg-amber-100 dark:bg-amber-800/40 px-1 rounded">[LINK]</span> with your article URL before posting.
                        </p>
                      </div>
                      {(() => {
                        const posts = effectiveSocialPosts.posts;
                        const platforms = ['twitter', 'facebook', 'telegram', 'instagram'];

                        // Platform character limits for display
                        const platformLimits: Record<string, number> = {
                          twitter: 280,
                          facebook: 500,
                          telegram: 4096,
                          instagram: 2200
                        };

                        return platforms.map((platform) => {
                          const platformPosts = posts.filter((p: any) =>
                            p.platform === platform ||
                            (typeof p === 'object' && p.platform === platform)
                          );

                          // Get the post text - handle both formats
                          let postText = '';
                          let hashtags: string[] = [];

                          if (platformPosts.length > 0) {
                            // New format: array of posts
                            postText = platformPosts[0].text || '';
                            hashtags = platformPosts[0].hashtags || [];
                          } else {
                            // Old format: check if posts object has platform keys with short/medium/long
                            const oldFormat = effectiveSocialPosts[platform];
                            if (oldFormat) {
                              // Pick the "medium" version as the single post (best balance)
                              postText = oldFormat.medium || oldFormat.short || oldFormat.long || '';
                            }
                          }

                          if (!postText) return null;

                          const platformLabels: Record<string, string> = {
                            twitter: 'X (Twitter)',
                            facebook: 'Facebook',
                            telegram: 'Telegram',
                            instagram: 'Instagram'
                          };

                          const platformColors: Record<string, string> = {
                            twitter: 'bg-zinc-100 dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700',
                            facebook: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
                            telegram: 'bg-sky-50 dark:bg-sky-900/20 border-sky-200 dark:border-sky-800',
                            instagram: 'bg-pink-50 dark:bg-pink-900/20 border-pink-200 dark:border-pink-800'
                          };

                          const charCount = postText.length;
                          const limit = platformLimits[platform] || 500;
                          const isOverLimit = charCount > limit;

                          return (
                            <div key={platform} className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 overflow-hidden">
                              <div className={`px-4 py-3 border-b ${platformColors[platform]}`}>
                                <div className="flex items-center justify-between">
                                  <h4 className="font-semibold text-zinc-900 dark:text-white">
                                    {platformLabels[platform] || platform}
                                  </h4>
                                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                                    isOverLimit
                                      ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                                      : 'bg-white/50 dark:bg-zinc-900/50 text-zinc-500 dark:text-zinc-400'
                                  }`}>
                                    {charCount}/{limit} chars
                                  </span>
                                </div>
                              </div>
                              <div className="p-4">
                                <div className="space-y-3">
                                  <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-lg p-4 border border-zinc-200 dark:border-zinc-700">
                                    <p className="text-sm leading-relaxed text-zinc-800 dark:text-zinc-200 whitespace-pre-wrap" dir={textDirection}>
                                      {postText.split('[LINK]').map((part: string, i: number, arr: string[]) => (
                                        <span key={i}>
                                          {part}
                                          {i < arr.length - 1 && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 mx-0.5 text-xs font-mono font-semibold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded border border-blue-200 dark:border-blue-700">[LINK]</span>
                                          )}
                                        </span>
                                      ))}
                                    </p>
                                    {hashtags.length > 0 && (
                                      <div className="mt-3 pt-3 border-t border-zinc-200 dark:border-zinc-700 flex flex-wrap gap-2">
                                        {hashtags.map((tag: string, idx: number) => (
                                          <span key={idx} className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded border border-blue-200 dark:border-blue-800">
                                            {tag.startsWith('#') ? tag : `#${tag}`}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <Button
                                    onClick={() => {
                                      navigator.clipboard.writeText(postText);
                                      setSuccessMsg('Copied to clipboard!');
                                      setTimeout(() => setSuccessMsg(null), 2000);
                                    }}
                                    variant="secondary"
                                    size="sm"
                                    className="w-full"
                                  >
                                    <Copy className="h-4 w-4 mr-2" />
                                    Copy to Clipboard
                                  </Button>
                                </div>
                              </div>
                            </div>
                          );
                        });
                      })()}

                      {/* Helper text */}
                      <p className="text-xs text-zinc-500 dark:text-zinc-400 text-center pt-2">
                        Copy, replace [LINK] with article URL, then post.
                      </p>
                    </div>
                  )}

                  {!hasSocialPosts && !generatingSocialPosts && currentStory.platform !== 'social' && (
                    <div className="text-center py-12 px-4 bg-zinc-50 dark:bg-zinc-800/30 rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700">
                      <Share2 className="h-8 w-8 text-zinc-400 dark:text-zinc-500 mx-auto mb-3" />
                      <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-1">
                        No social media posts generated yet
                      </p>
                      <p className="text-xs text-zinc-500 dark:text-zinc-500 mb-4">
                        Click the button above to create posts for all platforms
                      </p>
                      <Button
                        onClick={handleGenerateSocialPosts}
                        disabled={useTvEditor ? (!anchorIntro.trim() && !script.trim()) : (!headline.trim() || !body.trim())}
                        size="sm"
                        className="inline-flex items-center"
                      >
                        <Share2 className="h-4 w-4 mr-2" />
                        Generate Posts
                      </Button>
                    </div>
                  )}
                </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* AI Review Panel */}
        {showAiPanel && (
          <div className="w-80 flex-shrink-0 flex flex-col bg-white dark:bg-zinc-900 border-l border-zinc-200 dark:border-zinc-800">
            <div className="px-4 py-3 border-b border-zinc-200 dark:border-zinc-800">
              <h3 className="font-semibold text-zinc-800 dark:text-zinc-200 text-sm flex items-center">
                <Sparkles className="h-4 w-4 mr-2 text-purple-500" />
                AI Review
              </h3>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {aiLoading ? (
                <div className="flex flex-col items-center justify-center py-10">
                  <Loader2 className="h-8 w-8 animate-spin text-purple-500 mb-4" />
                  <p className="text-sm text-zinc-600 dark:text-zinc-400">{aiLoadingMsg}</p>
                </div>
              ) : aiIssues.length === 0 ? (
                <div className="text-center py-10">
                  <Check className="h-8 w-8 mx-auto text-green-500 mb-2" />
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">
                    No issues found!
                  </p>
                </div>
              ) : (() => {
                const pendingIssues = aiIssues.filter(i => !i.status || i.status === 'pending');
                const appliedCount = aiIssues.filter(i => i.status === 'applied').length;
                const dismissedCount = aiIssues.filter(i => i.status === 'dismissed').length;
                const pendingFixable = pendingIssues.filter(i => i.category === 'ai_fixable');
                const pendingHuman = pendingIssues.filter(i => i.category === 'human_required');

                const renderIssueBadges = (issue: EditorIssue) => (
                  (issue.type || issue.location) ? (
                    <div className="flex items-center gap-2 mb-1">
                      {issue.type && (
                        <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                          issue.type === 'grammar' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                          issue.type === 'demonym' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                          issue.type === 'format' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400' :
                          issue.type === 'terminology' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' :
                          issue.type === 'style' ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400' :
                          issue.type === 'attribution' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' :
                          issue.type === 'sourcing' ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400' :
                          issue.type === 'logic' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                          issue.type === 'completeness' ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400' :
                          issue.type === 'balance' ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400' :
                          issue.type === 'fact_check' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' :
                          'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400'
                        }`}>
                          {issue.type === 'fact_check' ? 'fact check' : issue.type}
                        </span>
                      )}
                      {issue.type === 'fact_check' && issue.confidence && (
                        <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                          issue.confidence === 'verified' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                          issue.confidence === 'unverified' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' :
                          'bg-gray-100 dark:bg-gray-900/30 text-gray-500 dark:text-gray-400'
                        }`}>
                          {issue.confidence}
                        </span>
                      )}
                      {issue.priority && (
                        <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                          issue.priority === 'critical' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                          issue.priority === 'high' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' :
                          issue.priority === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                          'bg-gray-100 dark:bg-gray-900/30 text-gray-500 dark:text-gray-400'
                        }`}>
                          {issue.priority}
                        </span>
                      )}
                      {issue.location && (
                        <span className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase">
                          in {issue.location}
                        </span>
                      )}
                    </div>
                  ) : null
                );

                const renderIssueCard = (issue: EditorIssue, bgClass: string, suggestionClass: string) => (
                  <div key={issue.id} className={`${bgClass} p-3 rounded-lg mb-2 group`}>
                    {renderIssueBadges(issue)}
                    <p className="text-sm text-zinc-700 dark:text-zinc-300">{issue.text}</p>
                    {issue.original && (
                      <p className="text-xs text-red-500/80 dark:text-red-400/80 mt-1 line-through">
                        &quot;{issue.original}&quot;
                      </p>
                    )}
                    {issue.suggestion && (
                      <p className={`text-xs mt-1 ${suggestionClass}`}>
                        &rarr; &quot;{issue.suggestion}&quot;
                      </p>
                    )}
                    {/* Dismiss / Mark Applied buttons */}
                    <div className="flex items-center gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleMarkIssueApplied(issue.id!)}
                        className="flex items-center gap-1 text-[10px] text-green-600 dark:text-green-400 hover:text-green-700 px-1.5 py-0.5 rounded hover:bg-green-50 dark:hover:bg-green-900/20"
                        title="Mark as addressed"
                      >
                        <Check className="h-3 w-3" /> Applied
                      </button>
                      <button
                        onClick={() => handleDismissIssue(issue.id!)}
                        className="flex items-center gap-1 text-[10px] text-zinc-500 dark:text-zinc-400 hover:text-red-500 px-1.5 py-0.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                        title="Dismiss this issue"
                      >
                        <X className="h-3 w-3" /> Dismiss
                      </button>
                    </div>
                  </div>
                );

                return (
                  <>
                    {/* AI Fixable (pending only) */}
                    {pendingFixable.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center mb-2">
                          <Sparkles className="h-3.5 w-3.5 text-yellow-500 mr-1.5" />
                          <span className="text-xs font-semibold text-yellow-700 dark:text-yellow-400 uppercase">
                            AI Can Fix ({pendingFixable.length})
                          </span>
                        </div>
                        {pendingFixable.map((issue) =>
                          renderIssueCard(issue, 'bg-yellow-50 dark:bg-yellow-900/20', 'text-green-600 dark:text-green-500')
                        )}
                      </div>
                    )}

                    {/* Human Required (pending only) */}
                    {pendingHuman.length > 0 && (
                      <div>
                        <div className="flex items-center mb-2">
                          <AlertTriangle className="h-3.5 w-3.5 text-orange-500 mr-1.5" />
                          <span className="text-xs font-semibold text-orange-700 dark:text-orange-400 uppercase">
                            Needs Your Attention ({pendingHuman.length})
                          </span>
                        </div>
                        {pendingHuman.map((issue) =>
                          renderIssueCard(issue, 'bg-orange-50 dark:bg-orange-900/20', 'text-orange-600 dark:text-orange-500 italic')
                        )}
                      </div>
                    )}

                    {/* Status summary */}
                    {(appliedCount > 0 || dismissedCount > 0) && pendingIssues.length === 0 && (
                      <div className="text-center py-4">
                        <Check className="h-6 w-6 mx-auto text-green-500 mb-1" />
                        <p className="text-xs text-zinc-500 dark:text-zinc-400">All issues addressed</p>
                      </div>
                    )}
                    {(appliedCount > 0 || dismissedCount > 0) && (
                      <div className="flex items-center gap-3 text-[10px] text-zinc-400 dark:text-zinc-500 mt-2 pt-2 border-t border-zinc-100 dark:border-zinc-800">
                        {appliedCount > 0 && <span className="text-green-500">{appliedCount} applied</span>}
                        {dismissedCount > 0 && <span className="text-zinc-400">{dismissedCount} dismissed</span>}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Action buttons */}
            <div className="p-4 border-t border-zinc-200 dark:border-zinc-800 space-y-2">
              {aiIssues.filter((i) => i.category === 'ai_fixable' && (!i.status || i.status === 'pending')).length > 0 && (
                <Button
                  onClick={handleApplyFixes}
                  disabled={aiLoading}
                  variant="outline"
                  className="w-full bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-900/30 hover:bg-yellow-100 dark:hover:bg-yellow-900/30"
                >
                  <Sparkles className="h-4 w-4 inline mr-2" />
                  Apply AI Fixes
                </Button>
              )}

              <Button
                onClick={() => {
                  setShowAiPanel(false);
                  setShowDiff(false);
                  // Don't clear issues - they persist
                }}
                variant="secondary"
                className="w-full"
              >
                <RotateCcw className="h-4 w-4 inline mr-2" />
                Continue Editing
              </Button>

              {/* Submit to Editor button in AI panel - prominent for reviewed stories */}
              {currentStory && currentStory.status === 'reviewed' && (
                <Button
                  onClick={handleSubmitForReview}
                  disabled={saving || submittingForReview}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {submittingForReview ? (
                    <Loader2 className="h-4 w-4 inline mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 inline mr-2" />
                  )}
                  Submit to Editor
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Editor Review Panel */}
        {showEditorReviewPanel && currentStory && (
          <EditorReviewPanel
            storyId={currentStory.id}
            language={currentStory.language}
            isTvStory={!!useTvEditor}
            authorName={undefined /* populated from profiles if needed */}
            submittedAt={currentStory.submittedForReviewAt}
            version={currentStory.version}
            saving={saving}
            aiIssues={aiIssues}
            editorFeedback={editorFeedback}
            onAddFeedback={handleAddEditorFeedback}
            onRemoveFeedback={handleRemoveEditorFeedback}
            onApprove={handleEditorApprove}
            onReject={handleEditorReject}
            onClose={() => setShowEditorReviewPanel(false)}
            getTextSelection={getEditorTextSelection}
          />
        )}

        {/* Version History Panel */}
        {showVersionHistory && currentStory && (
          <div className="w-96 flex-shrink-0 animate-slide-in-from-right">
            <VersionHistory
              storyId={currentStory.id}
              language={currentStory.language}
              isTvStory={!!useTvEditor}
              isLocked={isLocked}
              currentFields={{ headline, deck, body, slug, anchorIntro, script }}
              onRestore={handleRestoreRevision}
              onClose={() => setShowVersionHistory(false)}
            />
          </div>
        )}
      </div>

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white/50 dark:bg-zinc-900/50 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      )}

      {aiToast}
      {showLanguageSelector && selectedGroup && (
        <LanguageSelector
          group={selectedGroup}
          onSelectLanguage={handleSelectLanguage}
          onCreateTranslation={handleCreateTranslationFromGroup}
          onClose={() => {
            setShowLanguageSelector(false);
            setSelectedGroup(null);
          }}
        />
      )}
    </div>
  );
};

export default WritingTool;
